import * as i0 from '@angular/core';
import { Injectable, PLATFORM_ID, Directive, Inject, isDevMode, Input, EventEmitter, Output, InjectionToken, Optional, ContentChildren, HostListener, Pipe, forwardRef, SkipSelf, NgModule } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { distinctUntilChanged, map, filter, takeWhile, catchError, flatMap, finalize } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import dayjs__default from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as num2text from 'number-to-words';
import * as i1 from '@angular/forms';
import { Validators, UntypedFormGroup, UntypedFormArray, NG_VALUE_ACCESSOR, COMPOSITION_BUFFER_MODE } from '@angular/forms';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as i2 from 'rxjs';
import { Subject, BehaviorSubject, of, combineLatest, iif, isObservable, throwError } from 'rxjs';
import { isPlatformServer, isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import * as i1$1 from '@angular/router';
import { NavigationEnd, RouterLink } from '@angular/router';
import * as i1$2 from '@angular/platform-browser';
import { ɵgetDOM } from '@angular/platform-browser';
import pluralize from 'pluralize';
import tabbable from 'tabbable';
import 'reflect-metadata';
import { createTextMaskInputElement } from 'text-mask-core';

// import { Attribute as HtmlAttribute } from '@angular/compiler';
//
// // ContentWalker sets the ngContent @Input for us so we have the content as a raw string
class ContentWalker {
    //   static walker: ContentWalker;
    //
    //   // If we type parser as "Parser" angular doesn't like us touching its prototype, so "any" it is
    constructor(tags) {
        this.tags = tags;
    }
}

class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}

// from https://github.com/angular/angular/blob/2.4.10/modules/%40angular/facade/src/lang.ts#L71
function isPresent(obj) {
    return obj != null;
}
// from https://github.com/angular/angular/blob/2.4.10/modules/%40angular/facade/src/lang.ts#L75
function isBlank(obj) {
    return obj == null;
}

dayjs__default.extend(utc);
const CONTENT_TYPE = 'Content-Type';
const CONTENT_TYPE_JSON = 'application/json; charset=utf-8';
class HttpUtils {
    static hasContentTypeHeader(response) {
        return response.headers
            && response.headers.has(CONTENT_TYPE);
    }
    static isContentTypeJson(response) {
        // We have to use a regex to accommodate things like 'application/problem+json'
        const json_regex = /application\/(\w+\+)?json/gi;
        return HttpUtils.hasContentTypeHeader(response)
            && json_regex.test(response.headers.get(CONTENT_TYPE));
    }
    static isRequestingJson(request) {
        return request.responseType === 'json';
    }
    // Used to rehydrate datestamps to actual date objects in serialized JSON response
    static dateReviver(key, value) {
        if (typeof key === 'string') {
            if (key === 'utcDate' || key.endsWith('UtcDate')) {
                return dayjs__default(value).utc().toDate();
            }
            else if (key === 'date' || key.endsWith('Date')) {
                return dayjs__default(value).toDate();
            }
        }
        return value;
    }
    // This strips all nulls recursively, quickly
    static stripObjectNulls(obj) {
        // Saving this function to a variable due to this bug:
        // https://github.com/ng-packagr/ng-packagr/issues/696
        let replacer = (k, v) => v === null ? undefined : v;
        return JSON.parse(JSON.stringify(obj, replacer));
    }
    // This manually loops the top level (good for form data with uploadable files)
    static removeObjectNulls(obj) {
        let values = obj;
        Object.keys(values).forEach(key => {
            if (values[key] === null) {
                delete values[key];
            }
        });
        return values;
    }
}

// Expand on this list as requirements dictate.
const WORDS_TO_DISREGARD = {
    titleCase: ['a', 'an', 'and', 'to'],
};
const mapper = (str, isWord) => {
    if (!str || str === str.toUpperCase()) {
        return str;
    }
    if (WORDS_TO_DISREGARD.titleCase.includes(str.toLowerCase())) {
        return str.toLowerCase();
    }
    return (str.charAt(0).toUpperCase() +
        (isWord ? str.substr(1).toLowerCase() : str.substr(1)));
};
class TextUtils {
    static toTitleCase(value) {
        if (!value) {
            return value;
        }
        return value
            .split(' ')
            .map(str => mapper(str, true))
            .join(' ')
            .split('-')
            .map(str => mapper(str))
            .join('-');
    }
}

class NumberUtils {
    static parseFloat(value) {
        if (typeof value === 'string') {
            if (value.length === 0) {
                return '';
            }
            return parseFloat(value.replace(/[^\d.]+/g, ''));
        }
        return value;
    }
    static isSequential(value) {
        let str = `${value}`;
        if (str.length < 2) {
            return false;
        }
        let digitsOnly = NumberUtils.getOnlyDigits(str);
        for (let i = 1, len = digitsOnly.length; i < len; i += 1) {
            let first = parseInt(digitsOnly[i - 1], 10);
            let second = parseInt(digitsOnly[i], 10);
            let nextExpected = first + 1;
            if (nextExpected >= 10) {
                nextExpected = 0;
            }
            if (nextExpected !== second) {
                return false;
            }
        }
        return true;
    }
    static checkRepeated(str, maxLength = -1, digitsOnly = false) {
        if (digitsOnly) {
            str = NumberUtils.getOnlyDigits(str);
        }
        if (str && str.length < 2) {
            return false;
        }
        if (maxLength === -1) {
            maxLength = str.length;
        }
        for (let i = 1; i < maxLength; i += 1) {
            if (str[i - 1] !== str[i]) {
                return false;
            }
        }
        return true;
    }
    static getOnlyDigits(str) {
        return `${str}`.replace(new RegExp('/[^\d]/g'), '');
    }
    // Takes a number and outputs the length as formatted currency (for maxLength validation)
    static getCurrencyLength(num) {
        let numLength = num.toString().length;
        // Start with 3 to account for .00
        let addition = 3;
        // Add 1 for every group of 3 numbers to account for commas (,)
        addition += Math.floor(numLength / 3);
        // If the length is divisible by 3, drop 1 for the unnecessary comma
        addition -= (numLength % 3 === 0) ? 1 : 0;
        return numLength + addition;
    }
    // Takes an array of values and subtracts their combined length from a total max length
    static getRemainingLength(thisValue = '', otherValues = [], maxLength = 36) {
        let thisLength = thisValue?.length || 0;
        let otherLengths = otherValues
            .reduce((totalLength, value) => {
            let valueLength = value?.length || 0;
            return totalLength + valueLength;
        }, 0);
        return maxLength - otherLengths;
    }
    static numberToText(num) {
        let [dollars, cents] = num.split('.');
        cents = cents || '0';
        // Convert it to Title Case
        const txt = TextUtils.toTitleCase(num2text.toWords(dollars));
        return `${txt} Dollars And ${cents.padStart(2, '0')}/100`;
    }
}

class Unsubscriber {
    constructor() {
        this.subscriptions = [];
        // Redefine ngOnDestroy to tack on our custom unsubscribeAll function
        let ngOnDestroy = this.ngOnDestroy;
        this.ngOnDestroy = () => {
            ngOnDestroy();
            this.unsubscribeAll();
        };
    }
    unsubscribeAll() {
        this.subscriptions.forEach(sub => {
            if (!sub.closed) {
                sub.unsubscribe();
            }
        });
    }
    static { this.ɵfac = function Unsubscriber_Factory(t) { return new (t || Unsubscriber)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: Unsubscriber, factory: Unsubscriber.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Unsubscriber, [{
        type: Injectable
    }], function () { return []; }, null); })();

dayjs__default.extend(customParseFormat);
// @dynamic - This is needed here or else the aot compiler will freak out over using 'static' with regex patterns -- @ytilis
class ExtendedValidators {
    static { this._zipCodeRegEx = /^\d{5}(-\d{4})?$/; }
    static { this._phoneRegEx = /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s\.]{0,1}[0-9]{3}[-\s\.]{0,1}[0-9]{4}$/; }
    // eslint-disable-next-line
    static { this._emailRegEx = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; }
    static { this._passwordRegEx = /^.*(?=.{8,})(?=.*[a-z])(?=.*[0-9])(?=.*[^a-zA-Z\d\s])[a-zA-Z0-9\W\_]*$/; }
    static { this._ssnRegEx = /^\d{3}-?\d{2}-?\d{4}$/; }
    static { this._maskedSsnRegEx = /^[X•]{3}[ -]*[X•]{2}[ -]*\d{4}$/; }
    static { this._yearRegEx = /^\d{4}$/; }
    static { this._numberRegEx = /^([\d]+)$/; }
    static { this._floatRegEx = /^([\d\.]+)$/; }
    static { this._whitespace = /^\s*$/; }
    static { this._characterOrWhitespace = /^[a-zA-Z\s]*$/; }
    // if a control returns with as invalid when required we don't want to run another validator
    static isRequiredAndInvalid(control) {
        return isPresent(Validators.required(control));
    }
    static getDate(dateString) {
        const date = dayjs__default(dateString, 'MM/DD/YYYY', true);
        return (date?.isValid()) ? date : null;
    }
    static isBlankInputValue(value) {
        return value == null || typeof value === 'string' && (value.length === 0 || ExtendedValidators._whitespace.test(value));
    }
    // static atLeastOneCheckboxRequired
    static checkboxRequired(group) {
        let valid = false;
        // eslint-disable-next-line
        for (let name in group.controls) {
            let val = group.controls[name].value;
            if (val) {
                valid = true;
                break;
            }
        }
        if (valid) {
            return null;
        }
        return { checkboxRequired: true };
    }
    static charactersOnly(control) {
        if (ExtendedValidators.isRequiredAndInvalid(control)) {
            return null;
        }
        return ExtendedValidators._characterOrWhitespace.test(control.value)
            ? null
            : { 'charactersOnly': true };
    }
    static float(control) {
        if (ExtendedValidators.isRequiredAndInvalid(control)) {
            return null;
        }
        return ExtendedValidators._floatRegEx.test(control.value) ?
            null : { 'float': true };
    }
    static number(control) {
        if (ExtendedValidators.isRequiredAndInvalid(control)) {
            return null;
        }
        return ExtendedValidators._numberRegEx.test(control.value) ?
            null : { 'number': true };
    }
    static minvalue(value) {
        return (control) => {
            if (ExtendedValidators.isRequiredAndInvalid(control)) {
                return null;
            }
            return (value <= control.value) ? null : { 'minvalue': true };
        };
    }
    static repeated(maxLength = -1) {
        return (control) => {
            if (ExtendedValidators.isRequiredAndInvalid(control)) {
                return null;
            }
            let str = control.value;
            if (NumberUtils.checkRepeated(str, maxLength)) {
                return { 'repeated': true };
            }
            return null;
        };
    }
    static sequential(control) {
        if (ExtendedValidators.isRequiredAndInvalid(control)) {
            return null;
        }
        if (NumberUtils.isSequential(control.value)) {
            return { 'sequential': true };
        }
        return null;
    }
    static callback(callback, key = 'callback') {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            return callback(control.value) ? null : { [key]: true };
        };
    }
    static maxvalue(value) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            return (value >= control.value) ? null : { 'maxvalue': true };
        };
    }
    static zipCode(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators._zipCodeRegEx.test(control.value) ?
            null : { 'zipcode': true };
    }
    static phone(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators._phoneRegEx.test(control.value) ?
            null : { 'phone': true };
    }
    static date(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return (ExtendedValidators.getDate(control.value)) ? null : { 'date': true };
    }
    static futureDate(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        const enteredDate = ExtendedValidators.getDate(control.value);
        if (enteredDate?.isAfter(dayjs__default())) {
            return { 'futuredate': true };
        }
        return null;
    }
    static pastDate(date = new Date()) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            const enteredDate = ExtendedValidators.getDate(control.value);
            if (enteredDate?.isBefore(dayjs__default(date))) {
                return { 'pastdate': true };
            }
            return null;
        };
    }
    static maximumAge(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        const enteredDate = ExtendedValidators.getDate(control.value);
        if (enteredDate) {
            const oneHundredTenYearsAgo = dayjs__default().subtract(110, 'years');
            if (enteredDate.isBefore(oneHundredTenYearsAgo)) {
                return { 'maximumage': true };
            }
        }
        return null;
    }
    static ssn(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        if (ExtendedValidators._maskedSsnRegEx.test(control.value)) {
            return null;
        }
        return ExtendedValidators._ssnRegEx.test(control.value)
            ? null
            : { 'ssn': true };
    }
    static email(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators._emailRegEx.test(control.value) ?
            null : { 'email': true };
    }
    static year(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators._yearRegEx.test(control.value) ?
            null : { 'year': true };
    }
    static password(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators._passwordRegEx.test(control.value) ?
            null : { 'password': true };
    }
    static equalTo(compareControl) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            return control.value === compareControl.value ?
                null : { 'equalto': true };
        };
    }
    static maximumValue(value) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            return NumberUtils.parseFloat(control.value) <= value ?
                null : { 'maximumValue': true };
        };
    }
    static minimumValue(value) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            return NumberUtils.parseFloat(control.value) >= value ?
                null : { 'minimumValue': true };
        };
    }
    static minimumPercentageValue(total, minimum) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            let percentage = NumberUtils.parseFloat(control.value) * .01;
            let value = total * percentage;
            return value >= minimum ?
                null : { 'minimumPercentageValue': true };
        };
    }
    static minimumValueOrZero(value) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            let _ctrlVal = NumberUtils.parseFloat(control.value);
            return (0 === _ctrlVal || _ctrlVal >= value) ?
                null : { 'minimumValue': { minValue: value } };
        };
    }
    static maximumValueBasedOnAnother(value, compareControl) {
        return (control) => {
            if (isPresent(Validators.required(control))) {
                return null;
            }
            let compareValue = NumberUtils.parseFloat(compareControl.value) || 0;
            return NumberUtils.parseFloat(control.value) <= (value - compareValue) ?
                null : { 'maximumValueBasedOnAnother': true };
        };
    }
    static requiredIfChecked(checkbox) {
        return (control) => {
            if (checkbox.value === true) {
                return isBlank(control.value) ?
                    { 'requiredIfChecked': true } :
                    null;
            }
            else {
                return null;
            }
        };
    }
    static requiredIf(callback, validator) {
        return (control) => {
            if (callback()) {
                return validator(control);
            }
            else {
                return null;
            }
        };
    }
    // checkboxRequired
    static isTrue(control) {
        if (control.value === true) {
            return null;
        }
        return { 'required': true };
    }
    /**
     * Validator that requires controls to have a non-blank (whitespace) value; empty or missing value is not considered here.
     * If the field is also required (non-empty), use in conjuction with required validator.
     * This validator should not be together with requiredNonBlank as they overlap and duplicate functionality and validation error key.
    */
    static nonBlank(control) {
        if (isPresent(Validators.required(control))) {
            return null;
        }
        return ExtendedValidators.isBlankInputValue(control.value) ? { 'nonblank': true } : null;
    }
    /**
     * Validator that requires controls to have a non-empty (required) and non-blank (whitespace) value.
     * Should not be used in conjunction with required validator unless you want both validation keys to be added to control.
     * This validator should not be together with nonBlank as they overlap and duplicate functionality and validation error key.
     */
    static requiredNonBlank(control) {
        return ExtendedValidators.isBlankInputValue(control.value) ? { 'nonblank': true } : null;
    }
}

const dollarSign = '$';
const emptyString$1 = '';
const comma = ',';
const period = '.';
const minus = '-';
const minusRegExp = /-/;
const nonDigitsRegExp = /\D+/g;
const number = 'number';
const digitRegExp = /\d/;
const caretTrap = '[]';
function createNumberMask({ prefix = dollarSign, suffix = emptyString$1, includeThousandsSeparator = true, thousandsSeparatorSymbol = comma, allowDecimal = false, decimalSymbol = period, decimalLimit = 2, requireDecimal = false, allowNegative = false, allowLeadingZeroes = false, integerLimit = null } = {}) {
    const prefixLength = prefix && prefix.length || 0;
    const suffixLength = suffix && suffix.length || 0;
    const thousandsSeparatorSymbolLength = thousandsSeparatorSymbol && thousandsSeparatorSymbol.length || 0;
    function numberMask(rawValue = emptyString$1) {
        const rawValueLength = rawValue.length;
        if (rawValue === emptyString$1 ||
            (rawValue[0] === prefix[0] && rawValueLength === 1)) {
            //@ts-ignore
            return prefix.split(emptyString$1).concat([digitRegExp]).concat(suffix.split(emptyString$1));
        }
        else if (rawValue === decimalSymbol &&
            allowDecimal) {
            //@ts-ignore
            return prefix.split(emptyString$1).concat(['0', decimalSymbol, digitRegExp]).concat(suffix.split(emptyString$1));
        }
        const isNegative = (rawValue[0] === minus) && allowNegative;
        //If negative remove "-" sign
        if (isNegative) {
            rawValue = rawValue.toString().substr(1);
        }
        const indexOfLastDecimal = rawValue.lastIndexOf(decimalSymbol);
        const hasDecimal = indexOfLastDecimal !== -1;
        let integer;
        let fraction;
        let mask;
        // remove the suffix
        if (rawValue.slice(suffixLength * -1) === suffix) {
            rawValue = rawValue.slice(0, suffixLength * -1);
        }
        if (hasDecimal && (allowDecimal || requireDecimal)) {
            integer = rawValue.slice(rawValue.slice(0, prefixLength) === prefix ? prefixLength : 0, indexOfLastDecimal);
            fraction = rawValue.slice(indexOfLastDecimal + 1, rawValueLength);
            fraction = convertToMask(fraction.replace(nonDigitsRegExp, emptyString$1));
        }
        else {
            if (rawValue.slice(0, prefixLength) === prefix) {
                integer = rawValue.slice(prefixLength);
            }
            else {
                integer = rawValue;
            }
        }
        if (integerLimit && typeof integerLimit === number) {
            const thousandsSeparatorRegex = thousandsSeparatorSymbol === '.' ? '[.]' : `${thousandsSeparatorSymbol}`;
            const numberOfThousandSeparators = (integer.match(new RegExp(thousandsSeparatorRegex, 'g')) || []).length;
            integer = integer.slice(0, integerLimit + (numberOfThousandSeparators * thousandsSeparatorSymbolLength));
        }
        integer = integer.replace(nonDigitsRegExp, emptyString$1);
        if (!allowLeadingZeroes) {
            integer = integer.replace(/^0+(0$|[^0])/, '$1');
        }
        integer = (includeThousandsSeparator) ? addThousandsSeparator(integer, thousandsSeparatorSymbol) : integer;
        mask = convertToMask(integer);
        if ((hasDecimal && allowDecimal) || requireDecimal === true) {
            if (rawValue[indexOfLastDecimal - 1] !== decimalSymbol) {
                mask.push(caretTrap);
            }
            mask.push(decimalSymbol, caretTrap);
            if (fraction) {
                if (typeof decimalLimit === number) {
                    fraction = fraction.slice(0, decimalLimit);
                }
                mask = mask.concat(fraction);
            }
            if (requireDecimal === true && rawValue[indexOfLastDecimal - 1] === decimalSymbol) {
                mask.push(digitRegExp);
            }
        }
        if (prefixLength > 0) {
            mask = prefix.split(emptyString$1).concat(mask);
        }
        if (isNegative) {
            // If user is entering a negative number, add a mask placeholder spot to attract the caret to it.
            if (mask.length === prefixLength) {
                mask.push(digitRegExp);
            }
            mask = [minusRegExp].concat(mask);
        }
        if (suffix.length > 0) {
            mask = mask.concat(suffix.split(emptyString$1));
        }
        return mask;
    }
    numberMask.instanceOf = 'createNumberMask';
    return numberMask;
}
function convertToMask(strNumber) {
    return strNumber
        .split(emptyString$1)
        .map((char) => digitRegExp.test(char) ? digitRegExp : char);
}
// http://stackoverflow.com/a/10899795/604296
function addThousandsSeparator(n, thousandsSeparatorSymbol) {
    return n.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparatorSymbol);
}

const maxValueMonth = [31, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const formatOrder = ['yyyy', 'yy', 'mm', 'dd', 'HH', 'MM', 'SS'];
function createAutoCorrectedDatePipe(dateFormat = 'mm dd yyyy', { minYear = 1, maxYear = 9999 } = {}) {
    const dateFormatArray = dateFormat
        .split(/[^dmyHMS]+/)
        .sort((a, b) => formatOrder.indexOf(a) - formatOrder.indexOf(b));
    return function (conformedValue) {
        const indexesOfPipedChars = [];
        const maxValue = { 'dd': 31, 'mm': 12, 'yy': 99, 'yyyy': maxYear, 'HH': 23, 'MM': 59, 'SS': 59 };
        const minValue = { 'dd': 1, 'mm': 1, 'yy': 0, 'yyyy': minYear, 'HH': 0, 'MM': 0, 'SS': 0 };
        const conformedValueArr = conformedValue.split('');
        // Check first digit
        dateFormatArray.forEach((format) => {
            const position = dateFormat.indexOf(format);
            const maxFirstDigit = parseInt(maxValue[format].toString().substr(0, 1), 10);
            if (parseInt(conformedValueArr[position], 10) > maxFirstDigit) {
                conformedValueArr[position + 1] = conformedValueArr[position];
                conformedValueArr[position] = 0;
                indexesOfPipedChars.push(position);
            }
        });
        // Check for invalid date
        let month = 0;
        const isInvalid = dateFormatArray.some((format) => {
            const position = dateFormat.indexOf(format);
            const length = format.length;
            const textValue = conformedValue.substr(position, length).replace(/\D/g, '');
            const value = parseInt(textValue, 10);
            if (format === 'mm') {
                month = value || 0;
            }
            const maxValueForFormat = format === 'dd' ? maxValueMonth[month] : maxValue[format];
            if (format === 'yyyy' && (minYear !== 1 || maxYear !== 9999)) {
                const scopedMaxValue = parseInt(maxValue[format].toString().substring(0, textValue.length), 10);
                const scopedMinValue = parseInt(minValue[format].toString().substring(0, textValue.length), 10);
                return value < scopedMinValue || value > scopedMaxValue;
            }
            return value > maxValueForFormat || (textValue.length === length && value < minValue[format]);
        });
        if (isInvalid) {
            return false;
        }
        return {
            value: conformedValueArr.join(''),
            indexesOfPipedChars
        };
    };
}

let TextMaskConfig$1 = class TextMaskConfig {
};
class TextMasks {
    static getMask(key, maxLength) {
        let config = {
            mask: false,
            guide: false
        };
        switch (key) {
            case 'share':
                Object.assign(config, {
                    mask: createNumberMask({
                        prefix: '',
                        includeThousandsSeparator: false,
                        decimalLimit: 3,
                        allowDecimal: true,
                    }),
                    unmask: NumberUtils.parseFloat,
                });
                break;
            case 'ssn':
                Object.assign(config, {
                    mask: [/[\dX]/, /[\dX]/, /[\dX]/, '-', /[\dX]/, /[\dX]/, '-', /\d/, /\d/, /\d/, /\d/]
                });
                break;
            case 'ein':
                Object.assign(config, {
                    mask: [/[\dX]/, /[\dX]/, '-', /[\dX]/, /[\dX]/, /[\dX]/, /\d/, /\d/, /\d/, /\d/]
                });
                break;
            case 'currency':
                Object.assign(config, {
                    mask: createNumberMask({
                        prefix: '',
                        allowDecimal: true,
                    }),
                    unmask: NumberUtils.parseFloat,
                });
                break;
            case 'date':
                Object.assign(config, {
                    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
                    pipe: createAutoCorrectedDatePipe('mm/dd/yyyy'),
                });
                break;
            case 'phone':
                Object.assign(config, {
                    mask: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
                });
                break;
            case 'postal-code':
                Object.assign(config, {
                    mask: value => {
                        return value.length <= 5
                            ? [/\d/, /\d/, /\d/, /\d/, /\d/]
                            : [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
                    },
                });
                break;
            case 'whole-number':
                Object.assign(config, {
                    mask: createNumberMask({
                        prefix: '',
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                        integerLimit: maxLength
                    }),
                    showMask: false,
                });
                break;
            default:
                Object.assign(config, {
                    mask: false,
                });
        }
        return config;
    }
}

class SumdayTimer {
    constructor(ngZone, ms, instant = true) {
        this.timerSubject = new Subject();
        this.timer = this.timerSubject.asObservable()
            .pipe(
        // We only care about unique values, we don't want duplicate events when it restarts
        distinctUntilChanged());
        this.counter = 0;
        this.ngZone = ngZone;
        this.instant = instant;
        this.ms = ms;
        this.start();
    }
    run() {
        // We must run the timeout outside of Angular or else the state will never settle, and e2e tests will hang
        this.ngZone.runOutsideAngular(() => {
            this.timeout = setTimeout(() => this.ngZone.run(() => this.increment()), this.ms);
        });
    }
    increment() {
        this.timerSubject.next(this.counter++);
        this.run();
    }
    start() {
        if (!this.isRunning) {
            this.isRunning = true;
            if (this.instant) {
                this.increment();
            }
            else {
                this.run();
            }
        }
    }
    pause() {
        clearTimeout(this.timeout);
        this.isRunning = false;
    }
    stop() {
        this.pause();
        this.counter = 0;
    }
    restart() {
        this.stop();
        this.start();
    }
}

class ValidationService {
    static populateErrorsFromResponse(form, response) {
        let errors = response.error || response;
        if (typeof errors === 'string') {
            try {
                // It is possible that this function can get invoked with a blank response (timed out)
                errors = JSON.parse(errors);
            }
            catch (e) {
                console.error(`Error is not JSON:`, response);
            }
        }
        ValidationService.populateServerErrors(form, errors);
        return response;
    }
    /**
     * given an error response (validation or otherwise) from a server call, populate the form 'server' validation and catch-all errors.
     */
    static populateServerErrors(form, serverErrors, fieldNameMap) {
        // notes:  we are relying on the canonical form of errors returned from
        //         the underlying service.
        //         error.errors is an array of error objects; the current template only supports displaying one server error
        //         error.validationErrors is an object with field names as keys mapping to array of error objects;
        //             the current template only supports display of a single server error per field.
        if (serverErrors) {
            if (serverErrors.validationErrors) {
                for (let key in serverErrors.validationErrors) {
                    if (serverErrors.validationErrors.hasOwnProperty(key) && serverErrors.validationErrors[key] && key !== 'registrationId') {
                        let element = serverErrors.validationErrors[key];
                        if (element != null && element.length > 0) {
                            let formControl = ValidationService.getFormControlFromFormWithKey(form, key, fieldNameMap);
                            if (formControl) {
                                // assuming validation error is an array with at least one element. do we want to more strictly guard for this?
                                formControl.setErrors({ server: element[0].description });
                            }
                            else {
                                // assuming validation error is an array with at least one element. do we want to more strictly guard for this?
                                form.setErrors({ server: element[0].description });
                            }
                        }
                    }
                }
            }
            else if (serverErrors.errors && Array.isArray(serverErrors.errors)) {
                // assuming errors is an array with at least one element. do we want to more strictly guard for this?
                form.setErrors({ server: serverErrors.errors[0].description });
            }
            else {
                form.setErrors({ server: 'Our server dropped the ball. Try again.' });
            }
        }
        return form;
    }
    static getFormControlFromFormWithKey(form, key, fieldNameMap) {
        // perform key mapping if field name map defined.
        key = fieldNameMap && fieldNameMap[key] || key;
        // NOTE: form.get() allows for nested path queries (via dot notation or path array input),
        //       so no need to try and manually recurse down the tree
        let _control = form.get(key);
        return _control;
    }
    static validateFormAndDisplayErrors(form) {
        Object.keys(form.controls).map(controlName => {
            form.get(controlName).markAsTouched({ onlySelf: true });
        });
    }
}

const hasRequiredField = (abstractControl) => {
    if (abstractControl.validator) {
        const validator = abstractControl.validator({});
        if (validator && validator.required) {
            return true;
        }
    }
    if (abstractControl.controls) {
        for (const controlName in abstractControl.controls) {
            if (abstractControl.controls[controlName]) {
                if (hasRequiredField(abstractControl.controls[controlName])) {
                    return true;
                }
            }
        }
    }
    return false;
};

class DateInterceptor {
    constructor() { }
    intercept(request, next) {
        return next.handle(request)
            .pipe(map((event) => {
            if (event instanceof HttpResponse &&
                HttpUtils.isRequestingJson(request) &&
                HttpUtils.isContentTypeJson(event) &&
                event.body) {
                // We don't set responseType to 'text' for requests because Angular actually has a lot of logic
                // to handle JSON responses we don't want to have to reproduce. So we let it handle it so that
                // we can safely assume it's JSON by the time it gets here
                // @see: https://github.com/angular/angular/blob/5ae9b76a9b8618ebe0e734532c576e6d38ba72cd/packages/common/http/src/xhr.ts#L182
                event = event.clone({
                    body: JSON.parse(JSON.stringify(event.body), // We need to restringify it
                    HttpUtils.dateReviver // So we can parse it again with a date reviver
                    )
                });
            }
            return event;
        }));
    }
    static { this.ɵfac = function DateInterceptor_Factory(t) { return new (t || DateInterceptor)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DateInterceptor, factory: DateInterceptor.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DateInterceptor, [{
        type: Injectable
    }], function () { return []; }, null); })();

class FileInterceptor {
    constructor() {
        this.hasFiles = false;
    }
    intercept(request, next) {
        // Slightly hacky way of making sure that file upload forms always send form data, even when files are optional
        // Required for #10603, as dotnet can't dynamically switch between JSON and Form Data content for the same api endpoint
        this.hasFiles = request.reportProgress;
        if (request.body) {
            this.bodyFormData = new FormData();
            // Search for FileLists and rebuild as FormData if any are found
            this.buildFormData(request.body);
            if (this.hasFiles) {
                // We also want to send the formData instead of the original body
                this.body = this.bodyFormData;
                return next.handle(request.clone({
                    body: this.body,
                }));
            }
            else {
                // If there are no files we just leave it alone
                return next.handle(request);
            }
        }
        else {
            // If we don't have a body them just send back what we got
            return next.handle(request);
        }
    }
    buildFormData(data, key) {
        switch (true) {
            case data instanceof FileList:
                // If we've found files, flag the request, and append them as form data
                this.hasFiles = true;
                if (!key) {
                    throw new Error(`Files must have a form field name`);
                }
                for (let i = 0; i < data.length; i++) {
                    this.bodyFormData.append(key, data[i]);
                }
                break;
            case data instanceof Array:
                if (key) {
                    // If we got a nested array, check the contents and pass our current key to bind to them
                    for (let i = 0; i < data.length; i++) {
                        this.buildFormData(key, data[i]);
                    }
                }
                else {
                    // If there's no key then it's probably the root, so just short-circuit
                    this.bodyFormData = data;
                }
                break;
            case data instanceof FormData:
                if (key) {
                    this.bodyFormData.append(key, data);
                }
                else {
                    this.bodyFormData = data;
                }
                break;
            case data instanceof Object:
                // Loop through the object and check its contents
                Object.entries(data)
                    .forEach(([k, v]) => {
                    this.buildFormData(v, k);
                });
                break;
            default:
                if (key) {
                    this.bodyFormData.append(key, data);
                }
                else {
                    // If there's no key then it's probably the root, so just short-circuit
                    this.bodyFormData = data;
                }
                break;
        }
    }
    static { this.ɵfac = function FileInterceptor_Factory(t) { return new (t || FileInterceptor)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FileInterceptor, factory: FileInterceptor.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FileInterceptor, [{
        type: Injectable
    }], function () { return []; }, null); })();

class FormControlDirective {
    constructor(_element, ngControl, platformId) {
        this._element = _element;
        this.ngControl = ngControl;
        this.platformId = platformId;
    }
    ngAfterViewChecked() {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        let element = this._element.nativeElement;
        // If it's a component, find the input inside of it
        let input = ['input', 'textarea'].includes(element.tagName.toLowerCase())
            ? element
            : element.querySelector('input, textarea') || element;
        if (this.ngControl.touched && this.ngControl.invalid) {
            input.setAttribute('aria-invalid', true);
        }
        else {
            input.removeAttribute('aria-invalid');
        }
    }
    static { this.ɵfac = function FormControlDirective_Factory(t) { return new (t || FormControlDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.NgControl), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FormControlDirective, selectors: [["", "formControlName", ""], ["", "formControl", ""]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormControlDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[formControlName], [formControl]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i1.NgControl }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class FormGroupDirective {
    constructor(element, platformId, cd, controlContainer) {
        this.element = element;
        this.platformId = platformId;
        this.cd = cd;
        this.controlContainer = controlContainer;
        this.hasBoundEvents = false;
        this.hasBeenSubmitted = false;
        this.hasFocusedError = false;
        this.onSubmit = (event) => {
            this.hasBeenSubmitted = true;
            this.hasFocusedError = false;
            // if the user has submitted we want to force every control as touched so
            // the validation messages will show if they are in error
            this.markControlsAsTouched(this.controlContainer.control);
            this.cd.detectChanges();
            this.focusInvalidElementIfSubmitted();
        };
    }
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    ngOnInit() {
        if (!this.isBrowser) {
            return;
        }
        const formElement = this.element.nativeElement;
        if (!this.hasBoundEvents && formElement.tagName.toLowerCase() === 'form') {
            formElement.addEventListener('submit', this.onSubmit);
            this.hasBoundEvents = true;
        }
    }
    markControlsAsTouched(group) {
        Object.keys(group.controls).forEach((key) => {
            const abstractControl = group.controls[key];
            if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
                this.markControlsAsTouched(abstractControl);
            }
            else {
                abstractControl.markAsTouched();
            }
        });
    }
    getFirstInvalidElement() {
        let selectors = [
            'sumday-fieldset-list.ng-touched.ng-invalid input',
            'sumday-checkbox.ng-touched.ng-invalid .has-internal-validation input',
            'sumday-select.ng-touched.ng-invalid input',
            'sumday-text.ng-touched.ng-invalid input',
            'sumday-text.ng-touched.ng-invalid textarea',
            'input:not([type="hidden"]).ng-touched.ng-invalid',
            'button.ng-touched.ng-invalid',
            'select.ng-touched.ng-invalid'
        ];
        return this.element.nativeElement.querySelector(selectors.join(','));
    }
    focusInvalidElementIfSubmitted() {
        if (this.hasBeenSubmitted && !this.hasFocusedError) {
            let invalidElement = this.getFirstInvalidElement();
            if (invalidElement) {
                if (invalidElement.focus) {
                    invalidElement.focus();
                    this.hasFocusedError = true;
                }
                else {
                    isDevMode() && console.warn('Could not autofocus', invalidElement);
                }
            }
        }
    }
    ngOnDestroy() {
        if (!this.isBrowser) {
            return;
        }
        this.element.nativeElement.removeEventListener('submit', this.onSubmit);
    }
    static { this.ɵfac = function FormGroupDirective_Factory(t) { return new (t || FormGroupDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.ControlContainer)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FormGroupDirective, selectors: [["", "formGroup", ""]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FormGroupDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[formGroup]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: i0.ChangeDetectorRef }, { type: i1.ControlContainer }]; }, null); })();

function getWindow() {
    return window;
}
class WindowRefService {
    constructor(platformId) {
        this.platformId = platformId;
        this.resizeSubject = new BehaviorSubject(null);
        this.scrollSubject = new BehaviorSubject(null);
        this.events = {
            resize: this.resizeSubject.pipe(filter((state) => state != null)),
            scroll: this.scrollSubject.pipe(filter((state) => state != null))
        };
        if (this.isBrowser) {
            getWindow().addEventListener('resize', (e) => {
                this.resizeSubject.next(e);
            });
            getWindow().addEventListener('scroll', (e) => {
                this.scrollSubject.next(e);
            });
        }
    }
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    get nativeWindow() {
        return getWindow();
    }
    static { this.ɵfac = function WindowRefService_Factory(t) { return new (t || WindowRefService)(i0.ɵɵinject(PLATFORM_ID)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WindowRefService, factory: WindowRefService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowRefService, [{
        type: Injectable
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class SnapContainerDirective {
    constructor(_element, _windowRefService, platformId) {
        this._element = _element;
        this._windowRefService = _windowRefService;
        this.platformId = platformId;
        this.onScroll = (e) => {
            if (this.instances.length === 0) {
                return;
            }
            let scrollData = this.getScrollData();
            for (let instance of this.instances) {
                instance.onScrollData(scrollData);
            }
        };
        this.sendScrollData = (instance) => {
            let scrollData = this.getScrollData();
            instance.onScrollData(scrollData);
        };
        this.instances = [];
    }
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    getNativeElement() {
        return this._element.nativeElement;
    }
    getScrollData() {
        let { scrollTop } = this.scrollElement;
        // let { offsetTop, offsetWidth } = this._element.nativeElement;
        return {
            container: this._element.nativeElement,
            scrollTop
        };
    }
    ngOnInit() {
        if (!this.isBrowser) {
            return;
        }
        this.scrollElement = this.findScrollableElement();
        if (this.scrollElement) {
            if (this.scrollElement === document.body) {
                this._windowRefService.events.scroll.subscribe(this.onScroll);
            }
            else {
                this.scrollElement.addEventListener('scroll', this.onScroll);
            }
        }
    }
    findScrollableElement() {
        let current = { parentElement: this._element.nativeElement };
        let style = null;
        const next = () => {
            current = current.parentElement;
            style = this._windowRefService.nativeWindow.getComputedStyle(current);
        };
        next();
        while (!current || (style && style.overflow !== 'auto') && current !== document.body) {
            next();
        }
        return current;
    }
    ngOnDestroy() {
        if (!this.isBrowser || !this.scrollElement) {
            return;
        }
        this.scrollElement.removeEventListener('scroll', this.onScroll);
    }
    register(instance) {
        this.instances.push(instance);
        instance.registerScrollData(() => this.sendScrollData(instance));
    }
    unregister(target) {
        this.instances = this.instances.filter(instance => instance !== target);
    }
    static { this.ɵfac = function SnapContainerDirective_Factory(t) { return new (t || SnapContainerDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(WindowRefService), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: SnapContainerDirective, selectors: [["", "snapContainer", ""]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SnapContainerDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[snapContainer]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: WindowRefService }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class SnapDirective {
    constructor(_element, _snapContainerDirective, _windowRefService, platformId) {
        this._element = _element;
        this._snapContainerDirective = _snapContainerDirective;
        this._windowRefService = _windowRefService;
        this.platformId = platformId;
        this.bottomSnap = 'bottom-snap';
        this.snapEnabled = false;
        this.calculatePositioning = false;
        this.snapToContainer = false;
        this._originalElementHeight = 0;
        this.init = false;
        this._snapContainerDirective.register(this);
    }
    handleButtonSnap(element, scrollData) {
        let { clientHeight } = document.body;
        let elementRect = element.getBoundingClientRect();
        let diff = elementRect.top - clientHeight + elementRect.height;
        if (diff < 0) {
            this.stopSnap();
            return;
        }
        if (this._enabled) {
            this.updateSnap(scrollData);
        }
        else {
            this.startSnap(scrollData);
        }
    }
    handleContainerSnap(element, scrollData) {
        let elementRect = element.getBoundingClientRect();
        let containerStyles = this._windowRefService.nativeWindow.getComputedStyle(this._snapContainerDirective.getNativeElement());
        // if containerStyles.position === 'absolute' its the mobile style and we don't need to do this
        if (elementRect.top < 0 && containerStyles.position !== 'absolute') {
            if (this._enabled) {
                this.updateSnap(scrollData);
            }
            else {
                this.startSnap(scrollData);
            }
        }
        else {
            this.stopSnap();
        }
    }
    onScrollData(scrollData) {
        if (!this.snapEnabled) {
            this.stopSnap();
            return;
        }
        let element = this._enabled
            ? this._placeholderElement
            : this._element.nativeElement;
        if (this.snapToContainer) {
            this.handleContainerSnap(element, scrollData);
        }
        else {
            this.handleButtonSnap(element, scrollData);
        }
    }
    stopSnap() {
        if (!this._enabled) {
            return;
        }
        let parent = this._placeholderElement.parentElement;
        parent.removeChild(this._placeholderElement);
        this._placeholderElement = null;
        if (this.snap) {
            this._element.nativeElement.classList.remove(this.snap);
            this._element.nativeElement.classList.remove(this.bottomSnap);
        }
        // TODO: confirm absolute method will never be used.
        // this._element.nativeElement.style.position = '';
        // this._element.nativeElement.style.top = '';
        this._element.nativeElement.style.width = '';
        this._enabled = false;
    }
    updateSnap(scrollData) {
        if (!this._enabled) {
            return;
        }
        if (this.snapToContainer) {
            let { nativeWindow } = this._windowRefService;
            let parent = null;
            let parentRect = null;
            let elementRect = this._element.nativeElement.getBoundingClientRect();
            /*
              it sucks to have to do this on scroll but because of infinite scroll
              the height of parent can change
            */
            while (parentRect === null || parentRect.height === 0 || parentRect.height <= elementRect.height) {
                if (!parent) {
                    parent = scrollData.container.parentNode;
                }
                else {
                    parent = parent.parentNode;
                }
                parentRect = parent.getBoundingClientRect();
            }
            if (elementRect.height >= parentRect.height) {
                this.stopSnap();
                return;
            }
            if (parentRect.height + parentRect.top <= elementRect.height) {
                this._element.nativeElement.classList.add(this.bottomSnap);
            }
            else {
                this._element.nativeElement.classList.remove(this.bottomSnap);
            }
            if (elementRect.height > nativeWindow.innerHeight) {
                this.updateElementStyles({
                    overflowY: 'scroll',
                    height: `${(nativeWindow.innerHeight - elementRect.top)}px`
                });
                this._originalElementHeight = elementRect.height;
            }
            else if (this._originalElementHeight < nativeWindow.innerHeight) {
                this.updateElementStyles({
                    overflowY: 'visible',
                    height: 'auto'
                });
            }
        }
    }
    updateElementStyles(updates) {
        Object.assign(this._element.nativeElement.style, updates);
    }
    startSnap(scrollData) {
        if (this._enabled) {
            return;
        }
        this.updateStyles();
        this._placeholderElement = this.createPlaceholderElement();
        let parent = this._element.nativeElement.parentElement;
        parent.insertBefore(this._placeholderElement, this._element.nativeElement);
        this._element.nativeElement.style.width = this._styles.width;
        if (this.snap) {
            this._element.nativeElement.classList.add(this.snap);
        }
        // we have to enable before we update
        this._enabled = true;
        this.updateSnap(scrollData);
    }
    createPlaceholderElement() {
        let ele = document.createElement('div');
        let newStyles = {
            visibility: 'hidden',
            height: this._styles.height,
            width: this._styles.width,
        };
        Object.assign(ele.style, newStyles);
        return ele;
    }
    registerScrollData(fn) {
        this.triggerScrollData = fn;
    }
    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.init = true;
    }
    ngOnChanges(changes) {
        if (!this.init) {
            return;
        }
        if (changes.snapEnabled && changes.snapEnabled.currentValue) {
            this.triggerScrollData();
        }
        else {
            this.stopSnap();
        }
    }
    ngOnDestroy() {
        this._snapContainerDirective.unregister(this);
        this.init = false;
    }
    updateStyles() {
        let styles = this._windowRefService.nativeWindow.getComputedStyle(this._element.nativeElement);
        if (!styles) {
            return;
        }
        this._styles = {
            height: styles.height,
            width: styles.width,
        };
    }
    static { this.ɵfac = function SnapDirective_Factory(t) { return new (t || SnapDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SnapContainerDirective), i0.ɵɵdirectiveInject(WindowRefService), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: SnapDirective, selectors: [["", "snap", ""]], inputs: { snap: "snap", bottomSnap: "bottomSnap", snapEnabled: "snapEnabled", calculatePositioning: "calculatePositioning", snapToContainer: "snapToContainer" }, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SnapDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[snap]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: SnapContainerDirective }, { type: WindowRefService }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, { snap: [{
            type: Input
        }], bottomSnap: [{
            type: Input
        }], snapEnabled: [{
            type: Input
        }], calculatePositioning: [{
            type: Input
        }], snapToContainer: [{
            type: Input
        }] }); })();

class FocusMeDirective {
    constructor(element) {
        this.element = element;
        this.focusMe = false;
        this.focusParent = false;
    }
    ngOnChanges(changes) {
        if (changes.focusMe &&
            changes.focusMe.isFirstChange() === false &&
            changes.focusMe.currentValue &&
            changes.focusMe.previousValue === false) {
            this.focus();
        }
    }
    focus() {
        let elementToFocus;
        if (this.selector) {
            elementToFocus = this.element.nativeElement.querySelector(this.selector);
            if (!elementToFocus) {
                isDevMode() && console.warn(`${this.selector} was passed to focusMe but it was not found`);
            }
        }
        if (!elementToFocus) {
            elementToFocus = (this.focusParent) ? this.element.nativeElement.parentNode : this.element.nativeElement;
        }
        elementToFocus.focus();
    }
    ngAfterViewInit() {
        if (this.focusMe) {
            this.focus();
        }
    }
    static { this.ɵfac = function FocusMeDirective_Factory(t) { return new (t || FocusMeDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FocusMeDirective, selectors: [["", "focusMe", ""]], inputs: { focusMe: "focusMe", focusParent: "focusParent", selector: "selector" }, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FocusMeDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[focusMe]'
            }]
    }], function () { return [{ type: i0.ElementRef }]; }, { focusMe: [{
            type: Input
        }], focusParent: [{
            type: Input
        }], selector: [{
            type: Input
        }] }); })();

const BankAccountType = {
    Checking: 'checking',
    Savings: 'savings',
    Prepaid: 'prepaid',
};

class EmploymentStatus {
    static { this.Employed = 'employed'; }
    static { this.SelfEmployed = 'selfEmployed'; }
    static { this.NotEmployedRetired = 'notEmployedRetired'; }
}
const EmploymentStatusText = {
    [EmploymentStatus.Employed]: `Employed`,
    [EmploymentStatus.SelfEmployed]: `Self-employed`,
    [EmploymentStatus.NotEmployedRetired]: `Retired or Not Working`,
};
const EmploymentStatusDescription = {
    [EmploymentStatus.Employed]: `I'm employed`,
    [EmploymentStatus.SelfEmployed]: `I'm self-employed`,
    [EmploymentStatus.NotEmployedRetired]: `I'm retired or not working`,
};

var KeyCodes;
(function (KeyCodes) {
    KeyCodes[KeyCodes["TAB"] = 9] = "TAB";
    KeyCodes[KeyCodes["SPACEBAR"] = 32] = "SPACEBAR";
    KeyCodes[KeyCodes["ENTER"] = 13] = "ENTER";
    KeyCodes[KeyCodes["ESC"] = 27] = "ESC";
    KeyCodes[KeyCodes["ARROW_LEFT"] = 37] = "ARROW_LEFT";
    KeyCodes[KeyCodes["ARROW_UP"] = 38] = "ARROW_UP";
    KeyCodes[KeyCodes["ARROW_RIGHT"] = 39] = "ARROW_RIGHT";
    KeyCodes[KeyCodes["ARROW_DOWN"] = 40] = "ARROW_DOWN";
})(KeyCodes || (KeyCodes = {}));

var ChartType;
(function (ChartType) {
    ChartType["Pie"] = "pie";
    ChartType["Line"] = "line";
    ChartType["Area"] = "area";
    ChartType["Bar"] = "bar";
    ChartType["Column"] = "column";
})(ChartType || (ChartType = {}));

var NotificationType;
(function (NotificationType) {
    NotificationType["Default"] = "default";
    NotificationType["Promo"] = "promo";
    NotificationType["PromoGift"] = "promo-gift";
    NotificationType["Cta"] = "cta";
    NotificationType["Maintenance"] = "maintenance";
    NotificationType["Error"] = "error";
    NotificationType["Success"] = "success";
})(NotificationType || (NotificationType = {}));

var ToggleType;
(function (ToggleType) {
    ToggleType["Radio"] = "radio";
    ToggleType["Checkbox"] = "checkbox";
    ToggleType["Checklist"] = "checklist";
})(ToggleType || (ToggleType = {}));

var PlanType;
(function (PlanType) {
    PlanType["Able"] = "able";
    PlanType["CollegeSavings"] = "collegeSavings";
    PlanType["SecureChoice"] = "secureChoice";
})(PlanType || (PlanType = {}));

class RoleButtonDirective {
    constructor(element, platformId, renderer) {
        this.element = element;
        this.platformId = platformId;
        this.renderer = renderer;
        this.onKeyDown = (event) => {
            if (event.keyCode === KeyCodes.SPACEBAR) {
                event.preventDefault();
                this.element.nativeElement.click();
            }
            if (event.keyCode === KeyCodes.ENTER) {
                event.preventDefault();
                this.element.nativeElement.click();
            }
        };
    }
    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.element.nativeElement.addEventListener('keydown', this.onKeyDown);
            this.addTabIndex();
        }
    }
    addTabIndex() {
        if (!this.element.nativeElement.hasAttribute('tabindex')) {
            this.renderer.setAttribute(this.element.nativeElement, 'tabindex', '0');
        }
    }
    ngOnDestroy() {
        if (isPlatformBrowser(this.platformId)) {
            this.element.nativeElement.removeEventListener('keydown', this.onKeyDown);
        }
    }
    static { this.ɵfac = function RoleButtonDirective_Factory(t) { return new (t || RoleButtonDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: RoleButtonDirective, selectors: [["", "role", "button"]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoleButtonDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[role=button]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: i0.Renderer2 }]; }, null); })();

let id = 0;
class BaseMenuDirective {
    constructor(element, renderer, platformId) {
        this.element = element;
        this.renderer = renderer;
        this.platformId = platformId;
        this.id = `navbar-${id++}`;
        this.menu = this.element.nativeElement;
        this.topLevelItems = [];
        this.menuId = 0;
        this.menuEvents = [];
        this.navEvents = [];
        this.toggleTimers = {};
        this.escapeMenu = new EventEmitter();
        this.handleKeys = ($event) => {
            let topLevel = this.topLevelItems.includes($event.target);
            this.handleKeysImpl($event, topLevel);
        };
    }
    ngOnInit() {
        // Set an ID if there is none
        this.menu.id = this.menu.id || this.id;
    }
    ngAfterContentInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.bindEvents();
            this.parseMenus();
            // When the menu's child elements change, we want to reprocess everything
            this.contentWatcher = new MutationObserver(mutations => {
                this.topLevelItems = []; // We need to clear these because they may not exist anymore
                this.parseMenus();
            });
            this.contentWatcher.observe(this.menu, {
                childList: true,
                subtree: true,
            });
        }
    }
    ngOnDestroy() {
        if (isPlatformBrowser(this.platformId)) {
            this.destroyEvents(this.menuEvents);
        }
    }
    destroyEvents(events) {
        // Call every listener to destroy it
        events.map(call => call());
    }
    parseMenus() {
        // Destroy any listeners we've made so we don't double up
        this.destroyEvents(this.navEvents);
        this.processMenus();
        this.getAriaOrientation(this.menu);
    }
    processMenus() {
        // Remove default button styling from menu buttons
        for (let a of this.menu.querySelectorAll('button')) {
            a.classList.add('no-default-styles');
        }
        this.addAriaProperties();
        // Defines lists as menus, if not already defined
        for (let ul of this.menu.querySelectorAll('ul')) {
            this.addRoleToSubMenus(ul);
            // Set an ID if there is none
            ul.id = ul.id || `${this.id}-${this.menuId++}`;
            let toggle = ul.previousElementSibling;
            // If the toggle is an anchor or a button, then it's a nested menu
            if (toggle && ['A', 'BUTTON'].includes(toggle.tagName)) {
                // Bind them with aria attributes
                this.renderer.setAttribute(toggle, 'aria-haspopup', 'true');
                this.renderer.setAttribute(toggle, 'aria-controls', ul.id);
                this.renderer.setAttribute(toggle, 'aria-expanded', 'false');
                let closeTimer;
                this.navEvents = [
                    // Concatinate any existing events first
                    ...this.navEvents,
                    // Set expanded on hover
                    this.renderer.listen(ul.parentElement, 'mouseenter', event => {
                        clearTimeout(closeTimer);
                        this.toggleMenu(toggle, true);
                    }),
                    // Set closed on menu mouseleave
                    this.renderer.listen(ul.parentElement, 'mouseleave', event => {
                        closeTimer = setTimeout(() => this.toggleMenu(toggle, false), 50);
                    }),
                    // Toggle on click
                    this.renderer.listen(toggle, 'click', event => {
                        this.toggleMenu(toggle);
                    }),
                ];
            }
            else {
                // Make the menu tabbable to the first item
                this.setFirstFocusable(ul);
                // Save the menu items if it's top level
                this.topLevelItems = [
                    ...this.topLevelItems,
                    ...this.getMenuitems(ul)
                ];
            }
        }
    }
    bindEvents() {
        let closeTimer;
        this.menuEvents = [
            this.renderer.listen(this.menu, 'focusin', event => {
                clearTimeout(closeTimer);
            }),
            this.renderer.listen(this.menu, 'focusout', event => {
                closeTimer = setTimeout(() => this.closeMenus(this.menu), 50);
            }),
            this.renderer.listen(this.menu, 'keydown', event => {
                this.handleKeys(event);
            }),
        ];
    }
    getMenuitems(ul) {
        let items = [];
        for (let li of ul.children) {
            for (let a of li.children) {
                if (['A', 'BUTTON'].includes(a.tagName)) {
                    items.push(a);
                }
            }
        }
        return items;
    }
    toggleMenu(toggle, open, focusFirst) {
        let menuId = toggle.getAttribute('aria-controls');
        // If we don't get an 'open' param, then just toggle it
        open = (typeof (open) === 'boolean') ? open : (toggle.getAttribute('aria-expanded') === 'false');
        // Prevent race conditions that cause a double-toggle
        if (!this.toggleTimers[menuId]) {
            // Simple debouncer
            this.toggleTimers[menuId] = setTimeout(() => {
                clearTimeout(this.toggleTimers[menuId]);
                delete this.toggleTimers[menuId];
            }, 50);
            if (toggle.getAttribute('aria-haspopup')) {
                if (open) {
                    this.renderer.setAttribute(toggle, 'aria-expanded', 'true');
                    if (focusFirst) {
                        this.focusItem(toggle
                            .nextElementSibling // ul
                            .firstElementChild // li
                            .firstElementChild, // a/button
                        false);
                    }
                }
                else {
                    this.renderer.setAttribute(toggle, 'aria-expanded', 'false');
                }
            }
        }
    }
    closeMenus(ele) {
        for (let toggle of ele.querySelectorAll('[aria-expanded]')) {
            this.renderer.setAttribute(toggle, 'aria-expanded', 'false');
        }
    }
    getFocusedItem() {
        return document.activeElement;
    }
    getActiveTopLevelItem() {
        return this.menu.querySelector('[aria-expanded=true]');
    }
    focusItem(ele, setTabIndex) {
        // Per the spec example, we only want to set the tabindex on the root menu menu
        if (setTabIndex) {
            this.setRovingTabIndex(ele);
        }
        ele?.focus();
    }
    addRoleToSubMenus(ul) { } // only used for role=menubar
    setFirstFocusable(ele) { } // only used for role=menubar
    addAriaProperties() { } // only used for role=menubar
    getAriaOrientation(menu) { } // only used for role=menubar
    setRovingTabIndex(ele) { } // only used for role=menubar
    static { this.ɵfac = function BaseMenuDirective_Factory(t) { return new (t || BaseMenuDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: BaseMenuDirective, outputs: { escapeMenu: "escapeMenu" } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BaseMenuDirective, [{
        type: Directive
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, { escapeMenu: [{
            type: Output
        }] }); })();

class RoleMenubarDirective extends BaseMenuDirective {
    constructor(element, renderer, platformId) {
        super(element, renderer, platformId);
        this.element = element;
        this.renderer = renderer;
        this.platformId = platformId;
    }
    setFirstFocusable(ele) {
        this.setRovingTabIndex(ele.querySelector('li a, li button'));
    }
    getAriaOrientation(ele) {
        this.vertical = ele.getAttribute('aria-orientation') === 'vertical';
    }
    addAriaProperties() {
        // Removes the implied listitem role of the li element.
        // Necessary because the parent ul is serving as a menu so the li elements are not in their required list context.
        for (let li of this.menu.querySelectorAll('li:not([role])')) {
            li.setAttribute('role', 'presentation');
        }
        // Sets out links and buttons as menuitems
        for (let a of this.menu.querySelectorAll('li a:not([role]), li button:not([role])')) {
            a.setAttribute('role', 'menuitem');
        }
    }
    addRoleToSubMenus(ul) {
        // Add a menu role if it doesn't have one
        if (!ul.getAttribute('role')) {
            ul.setAttribute('role', 'menu');
        }
    }
    handleKeysImpl($event, isTopLevel) {
        let back = $event.keyCode === KeyCodes.ARROW_UP
            || $event.keyCode === KeyCodes.ARROW_LEFT;
        switch ($event.keyCode) {
            case KeyCodes.ARROW_DOWN:
            case KeyCodes.ARROW_UP:
                $event.preventDefault();
                $event.stopPropagation();
                if (isTopLevel) {
                    this.toggleMenu(this.getActiveTopLevelItem(), true, true);
                }
                else {
                    this.move(this.getFocusedItem(), back, isTopLevel);
                }
                break;
            case KeyCodes.ARROW_LEFT:
            case KeyCodes.ARROW_RIGHT:
                $event.preventDefault();
                $event.stopPropagation();
                this.closeMenus(this.menu);
                this.move(this.getActiveTopLevelItem(), back, true // Left and Right always move along the root
                );
                break;
            case KeyCodes.ENTER:
            case KeyCodes.SPACEBAR:
                $event.preventDefault();
                $event.stopPropagation();
                $event.target.click();
                break;
            case KeyCodes.ESC:
                if (isTopLevel) {
                    this.escapeMenu.emit();
                }
                else {
                    // Close open submenus
                    this.closeMenus(this.menu);
                    // Focus on the last active root item
                    this.focusItem(this.getActiveTopLevelItem(), false);
                }
                break;
        }
    }
    move(current, back = false, topLevel) {
        if (topLevel) {
            let index = this.topLevelItems.indexOf(current);
            if (back) {
                current = this.topLevelItems[index - 1] || this.topLevelItems[this.topLevelItems.length - 1];
            }
            else {
                current = this.topLevelItems[index + 1] || this.topLevelItems[0];
            }
        }
        else {
            let inList = current.parentElement.tagName === 'LI';
            if (inList) {
                current = current.parentElement;
            }
            if (back) {
                // Get the previous sibling, or the last item in the list
                current = (current.previousElementSibling || current.parentElement.lastElementChild);
            }
            else {
                // Get the next sibling, or the first item in the list
                current = (current.nextElementSibling || current.parentElement.firstElementChild);
            }
            if (inList) {
                current = current.querySelector('a, button');
            }
        }
        this.focusItem(current, topLevel);
    }
    getActiveTopLevelItem() {
        return this.topLevelItems.find(ele => ele.getAttribute('tabindex') === '0');
    }
    setRovingTabIndex(ele) {
        // Roving tabindex: https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex
        for (let a of this.menu.querySelectorAll('li a, li button')) {
            a.setAttribute('tabindex', a === ele ? '0' : '-1');
        }
    }
    static { this.ɵfac = function RoleMenubarDirective_Factory(t) { return new (t || RoleMenubarDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: RoleMenubarDirective, selectors: [["", "role", "menubar"]], features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoleMenubarDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[role=menubar]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class CloseMenuBtnDirective {
    constructor(element, renderer, platformId) {
        this.element = element;
        this.renderer = renderer;
        this.platformId = platformId;
        this.closeBtnEvents = [];
    }
    ngAfterContentInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.addCloseButton();
            // When the menu's child elements change, we want to reprocess everything
            this.contentWatcher = new MutationObserver(mutations => {
                this.addCloseButton();
            });
            this.contentWatcher.observe(this.element.nativeElement, {
                childList: true,
                subtree: true,
            });
        }
    }
    ngOnDestroy() {
        if (isPlatformBrowser(this.platformId)) {
            this.destroyEvents(this.closeBtnEvents);
        }
    }
    destroyEvents(events) {
        // Call every listener to destroy it
        events.forEach(call => call());
    }
    addCloseButton() {
        // Add a "Close menu" button to every sub-ul
        for (let ul of this.element.nativeElement.querySelectorAll('ul')) {
            if (ul.lastElementChild && !ul.lastElementChild.classList.contains('close-menu')) {
                let closeMenuLi = this.renderer.createElement('li');
                let closeMenuBtn = this.renderer.createElement('button');
                let closeMenuTxt = this.renderer.createText('Close menu');
                this.renderer.addClass(closeMenuLi, 'close-menu');
                let toggle = ul.previousElementSibling;
                this.closeBtnEvents = [
                    ...this.closeBtnEvents,
                    this.renderer.listen(closeMenuBtn, 'focus', event => this.renderer.addClass(closeMenuLi, 'focused')),
                    this.renderer.listen(closeMenuBtn, 'blur', event => this.renderer.removeClass(closeMenuLi, 'focused')),
                    this.renderer.listen(closeMenuBtn, 'click', event => {
                        this.renderer.removeAttribute(toggle, 'aria-expanded');
                        toggle.focus();
                    }),
                ];
                this.renderer.appendChild(closeMenuBtn, closeMenuTxt);
                this.renderer.appendChild(closeMenuLi, closeMenuBtn);
                this.renderer.appendChild(ul, closeMenuLi);
            }
        }
    }
    static { this.ɵfac = function CloseMenuBtnDirective_Factory(t) { return new (t || CloseMenuBtnDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: CloseMenuBtnDirective, selectors: [["", "sdCloseMenuBtn", ""]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CloseMenuBtnDirective, [{
        type: Directive,
        args: [{
                selector: '[sdCloseMenuBtn]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

const CDN_HOST = new InjectionToken(null);
const API_BASE_PATH = new InjectionToken(null);
const SUMDAY_COMMON_FORROOT_GUARD = new InjectionToken('SUMDAY_COMMON_FORROOT_GUARD');
const PAGE_TITLE_BASE_TEXT = new InjectionToken(null);
const H1_DEFAULT = new InjectionToken(null);

class CdnSrcDirective {
    set src(value) {
        if (!this.isBrowser) {
            return;
        }
        if (value !== this._src) {
            this._src = value;
            this.fallingBack = false;
            this.refreshImage(value);
        }
    }
    // private cdnHost: string = null;
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    constructor(_element, cdnHost, platformId, windowRefService) {
        this._element = _element;
        this.cdnHost = cdnHost;
        this.platformId = platformId;
        this.windowRefService = windowRefService;
        this.localHostName = null;
        this.attemptingCdn = false;
        this.fallingBack = false;
        this.onImageError = (e) => {
            if (!this.fallingBack) {
                if (this.attemptingCdn && this.cdnHost) {
                    // we are going to try the original value now without CDN
                    this.updateSrcWithoutCdn(this._src);
                }
                else if (this.fallbackUrl && this.fallbackUrl.length > 0) {
                    // if we aren't attempting CDN and we still failed, then the image won't load at all
                    // so we're going to fall back now.
                    this.fallingBack = true;
                    this.refreshImage(this.fallbackUrl);
                }
                else {
                    // we failed completely and don't have a fallback image.
                }
            }
            else {
                // if we are attempting a CDN load of the fall back and that fails, we will try one more time
                // on the local server, otherwise, we fail
                if (this.attemptingCdn) {
                    this.updateSrcWithoutCdn(this.fallbackUrl);
                }
                else {
                    // we completely failed even with the fallback image, nothing more we can do
                }
            }
        };
        if (!this.isBrowser) {
            return;
        }
        this._element.nativeElement.addEventListener('error', this.onImageError);
        let { nativeWindow } = this.windowRefService;
        let { protocol, hostname, port } = nativeWindow.location;
        this.localHostName = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    }
    ngOnDestroy() {
        if (!this.isBrowser) {
            return;
        }
        this._element.nativeElement.removeEventListener('error', this.onImageError);
    }
    refreshImage(src) {
        if ((this.cdnHost && this.cdnHost.length > 0) &&
            src.indexOf('/assets') === 0) {
            this.updateSrcWithCdn(src);
        }
        else {
            this.updateSrcWithoutCdn(src);
        }
    }
    updateSrcWithCdn(src) {
        this.attemptingCdn = true;
        this._element.nativeElement.src = (src.indexOf(this.localHostName) === 0)
            ? src.replace(this.localHostName, this.cdnHost)
            : `${this.cdnHost}${src}`;
    }
    updateSrcWithoutCdn(src) {
        this.attemptingCdn = false;
        this._element.nativeElement.src = src;
    }
    static { this.ɵfac = function CdnSrcDirective_Factory(t) { return new (t || CdnSrcDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(CDN_HOST, 8), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(WindowRefService)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: CdnSrcDirective, selectors: [["img", "src", ""]], inputs: { fallbackUrl: "fallbackUrl", src: "src" } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CdnSrcDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: 'img[src]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [CDN_HOST]
            }, {
                type: Optional
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: WindowRefService }]; }, { fallbackUrl: [{
            type: Input
        }], src: [{
            type: Input
        }] }); })();

class AriaActiveLinkDirective extends Unsubscriber {
    constructor(router, element, renderer) {
        super();
        this.router = router;
        this.element = element;
        this.renderer = renderer;
        this.routerLinkActiveOptions = { exact: false };
        this.subscriptions = [
            router.events.subscribe(s => {
                if (s instanceof NavigationEnd) {
                    this.update();
                }
            }),
        ];
    }
    get isActive() { return this.active; }
    ngAfterContentInit() {
        this.links.changes.subscribe(_ => this.update());
        this.linksWithHrefs.changes.subscribe(_ => this.update());
        this.update();
    }
    ngOnChanges(changes) { this.update(); }
    update() {
        if (!this.links || !this.linksWithHrefs || !this.router.navigated) {
            return;
        }
        const hasActiveLinks = this.hasActiveLinks();
        // react only when status has changed to prevent unnecessary dom updates
        if (this.active !== hasActiveLinks) {
            if (hasActiveLinks) {
                this.renderer.setAttribute(this.element.nativeElement, 'aria-current', 'page');
            }
            else {
                this.renderer.removeAttribute(this.element.nativeElement, 'aria-current');
            }
            Promise.resolve(hasActiveLinks).then(active => this.active = active);
        }
    }
    isLinkActive(router) {
        return (link) => router.isActive(link.urlTree, this.routerLinkActiveOptions.exact);
    }
    hasActiveLinks() {
        return this.links.some(this.isLinkActive(this.router)) ||
            this.linksWithHrefs.some(this.isLinkActive(this.router));
    }
    ngOnDestroy() { }
    static { this.ɵfac = function AriaActiveLinkDirective_Factory(t) { return new (t || AriaActiveLinkDirective)(i0.ɵɵdirectiveInject(i1$1.Router), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: AriaActiveLinkDirective, selectors: [["", "routerLinkActive", ""]], contentQueries: function AriaActiveLinkDirective_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, RouterLink, 5);
            i0.ɵɵcontentQuery(dirIndex, RouterLink, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.links = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.linksWithHrefs = _t);
        } }, inputs: { routerLinkActiveOptions: "routerLinkActiveOptions" }, features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AriaActiveLinkDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[routerLinkActive]'
            }]
    }], function () { return [{ type: i1$1.Router }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, { links: [{
            type: ContentChildren,
            args: [RouterLink, { descendants: true }]
        }], linksWithHrefs: [{
            type: ContentChildren,
            args: [RouterLink, { descendants: true }]
        }], routerLinkActiveOptions: [{
            type: Input
        }] }); })();

class NotificationService {
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    constructor(_window, platformId, cdnHost) {
        this._window = _window;
        this.platformId = platformId;
        this.cdnHost = cdnHost;
        if (!this.isBrowser) {
            return;
        }
        // To avoid getting null cast as a string further down
        this.cdnHost = this.cdnHost || '';
    }
    notify(title, body, icon) {
        if (!this.isBrowser) {
            return;
        }
        if (this._window.nativeWindow['Notification'].permission !== 'granted') {
            this._window.nativeWindow['Notification'].requestPermission();
        }
        else {
            try {
                let notification = new this._window.nativeWindow['Notification'](title, {
                    body: body,
                    icon: icon || `${this.cdnHost}/assets/images/favicon.png`,
                });
                notification.onclick = function () {
                    notification.close();
                };
            }
            catch (error) { }
        }
    }
    static { this.ɵfac = function NotificationService_Factory(t) { return new (t || NotificationService)(i0.ɵɵinject(WindowRefService), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(CDN_HOST, 8)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: NotificationService, factory: NotificationService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NotificationService, [{
        type: Injectable
    }], function () { return [{ type: WindowRefService }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [CDN_HOST]
            }, {
                type: Optional
            }] }]; }, null); })();

class ClipboardDirective {
    onMouseEnter() {
        this.copyToClipboard(this.clipboard);
    }
    get isBrowser() { return isPlatformBrowser(this.platformId); }
    constructor(_element, _window, notificationService, renderer, document, platformId) {
        this._element = _element;
        this._window = _window;
        this.notificationService = notificationService;
        this.renderer = renderer;
        this.document = document;
        this.platformId = platformId;
    }
    copyToClipboard(text) {
        if (!this.isBrowser) {
            return;
        }
        text = text.replace(/</g, '&lt;').replace('>', '&gt;');
        text = this.renderer.createText(text);
        let ele = this.renderer.createElement('pre');
        this.renderer.appendChild(ele, text);
        this.renderer.appendChild(this.document.body, ele);
        let selection = this._window.nativeWindow.getSelection();
        let range = this.document.createRange();
        range.selectNodeContents(ele);
        selection.removeAllRanges();
        selection.addRange(range);
        this.document.execCommand('Copy');
        selection.removeAllRanges();
        ele.remove();
        this.notificationService.notify(`Text copied`, `
      ${this.clipboardTitle || this.clipboard} has been copied to the clipboard`);
    }
    static { this.ɵfac = function ClipboardDirective_Factory(t) { return new (t || ClipboardDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(WindowRefService), i0.ɵɵdirectiveInject(NotificationService), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ClipboardDirective, selectors: [["", "clipboard", ""]], hostBindings: function ClipboardDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function ClipboardDirective_click_HostBindingHandler() { return ctx.onMouseEnter(); });
        } }, inputs: { clipboard: "clipboard", clipboardTitle: "clipboardTitle" } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ClipboardDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[clipboard]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: WindowRefService }, { type: NotificationService }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, { clipboard: [{
            type: Input
        }], clipboardTitle: [{
            type: Input
        }], onMouseEnter: [{
            type: HostListener,
            args: ['click']
        }] }); })();

const TITLE_TOKEN = '${pageTitle}';
class H1Directive {
    constructor(element, renderer, basePageTitle, titleService, h1Defaults) {
        this.element = element;
        this.renderer = renderer;
        this.basePageTitle = basePageTitle;
        this.titleService = titleService;
        this.h1Defaults = h1Defaults;
        this.updatePageTitle = null;
        this.autoFocus = null;
        this.focusTimer = null;
    }
    ngOnInit() {
        this.updatePageTitle = (this.updatePageTitle === null) ? this.h1Defaults.updatePageTitle : this.updatePageTitle;
        this.autoFocus = (this.autoFocus === null) ? this.h1Defaults.autoFocus : this.autoFocus;
    }
    ngAfterViewInit() {
        this.focusHeader();
    }
    ngAfterViewChecked() {
        this.setPageTitle();
    }
    focusHeader() {
        if (!this.autoFocus) {
            return;
        }
        clearTimeout(this.focusTimer);
        this.renderer.setAttribute(this.element.nativeElement, 'tabindex', '-1');
        // this timeout is because some screen readers need some time to build their accessibility tree
        // need to give it some time or it may fail to focus
        this.focusTimer = setTimeout(() => {
            this.element.nativeElement.focus();
        }, 400);
    }
    setPageTitle() {
        if (!this.updatePageTitle) {
            return;
        }
        let pageTitle = this.basePageTitle.getValue() || '';
        // if aria-label is set it should override the text thats actually in the tag
        // screen readers give aria-label the higest precedence
        const h1Text = this.element.nativeElement.getAttribute('aria-label') || this.element.nativeElement.textContent;
        if (h1Text) {
            if (pageTitle.includes(TITLE_TOKEN)) {
                pageTitle = pageTitle.replace(TITLE_TOKEN, h1Text);
            }
            else if (pageTitle.length > 0) {
                pageTitle = `${h1Text} - ${pageTitle}`;
            }
            else {
                pageTitle = h1Text;
            }
        }
        // only update the title if its different
        if (pageTitle !== this.titleService.getTitle()) {
            this.titleService.setTitle(pageTitle);
        }
    }
    ngOnDestroy() {
        clearTimeout(this.focusTimer);
    }
    static { this.ɵfac = function H1Directive_Factory(t) { return new (t || H1Directive)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PAGE_TITLE_BASE_TEXT), i0.ɵɵdirectiveInject(i1$2.Title), i0.ɵɵdirectiveInject(H1_DEFAULT)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: H1Directive, selectors: [["h1"]], inputs: { updatePageTitle: "updatePageTitle", autoFocus: "autoFocus" } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(H1Directive, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: 'h1'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i2.BehaviorSubject, decorators: [{
                type: Inject,
                args: [PAGE_TITLE_BASE_TEXT]
            }] }, { type: i1$2.Title }, { type: undefined, decorators: [{
                type: Inject,
                args: [H1_DEFAULT]
            }] }]; }, { updatePageTitle: [{
            type: Input
        }], autoFocus: [{
            type: Input
        }] }); })();

class TargetBlankDirective {
    constructor(element, platformId, renderer) {
        this.element = element;
        this.platformId = platformId;
        this.renderer = renderer;
        // this is not really ideal for when we have to do translations
        this.screenReaderText = 'opens a new window';
    }
    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            let span = this.renderer.createElement('span');
            this.renderer.addClass(span, 'screen-reader-only');
            let text = this.renderer.createText(` ( ${this.screenReaderText} )`);
            this.renderer.appendChild(span, text);
            this.renderer.appendChild(this.element.nativeElement, span);
        }
    }
    static { this.ɵfac = function TargetBlankDirective_Factory(t) { return new (t || TargetBlankDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: TargetBlankDirective, selectors: [["", "target", "_blank"]], inputs: { screenReaderText: "screenReaderText" } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TargetBlankDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[target=_blank]'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: i0.Renderer2 }]; }, { screenReaderText: [{
            type: Input
        }] }); })();

let generatedId = 0;
class ExpandableContainerDirective {
    constructor() {
        this.id = `expandable-${generatedId++}`;
    }
    get expandableItemId() {
        return this.id;
    }
    static { this.ɵfac = function ExpandableContainerDirective_Factory(t) { return new (t || ExpandableContainerDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ExpandableContainerDirective, selectors: [["", "expandableContainer", ""]] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpandableContainerDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[expandableContainer]'
            }]
    }], null, null); })();

class ExpandableContentDirective {
    constructor(expandableContainerDirective, elementRef, renderer) {
        this.expandableContainerDirective = expandableContainerDirective;
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'id', this.expandableContainerDirective.expandableItemId);
    }
    ngOnChanges(changes) {
        if (changes.open) {
            this.updateView();
        }
    }
    updateView() {
        const element = this.elementRef.nativeElement;
        this.renderer.setAttribute(element, 'aria-hidden', (!this.open).toString());
        if (this.open) {
            this.renderer.removeAttribute(element, 'hidden');
            this.renderer.removeClass(element, 'hide');
        }
        else {
            this.renderer.setAttribute(element, 'hidden', 'true');
            this.renderer.addClass(element, 'hide');
        }
    }
    static { this.ɵfac = function ExpandableContentDirective_Factory(t) { return new (t || ExpandableContentDirective)(i0.ɵɵdirectiveInject(ExpandableContainerDirective), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ExpandableContentDirective, selectors: [["", "expandableContent", ""]], inputs: { open: "open" }, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpandableContentDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[expandableContent]'
            }]
    }], function () { return [{ type: ExpandableContainerDirective }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, { open: [{
            type: Input
        }] }); })();

class NavDirective extends BaseMenuDirective {
    constructor(element, renderer, platformId) {
        super(element, renderer, platformId);
        this.element = element;
        this.renderer = renderer;
        this.platformId = platformId;
    }
    handleKeysImpl($event, isTopLevel) {
        switch ($event.keyCode) {
            case KeyCodes.ENTER:
            case KeyCodes.SPACEBAR:
                $event.preventDefault();
                $event.stopPropagation();
                $event.target.click();
                break;
            case KeyCodes.ESC:
                if (isTopLevel) {
                    this.escapeMenu.emit();
                }
                else {
                    // Focus on the last active root item
                    this.focusItem(this.getActiveTopLevelItem());
                    // Close open submenus
                    this.closeMenus(this.menu);
                }
                break;
        }
    }
    static { this.ɵfac = function NavDirective_Factory(t) { return new (t || NavDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: NavDirective, selectors: [["nav"]], features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NavDirective, [{
        type: Directive,
        args: [{
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: 'nav'
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class Address {
}

class ActivityParams {
}
const DEFAULT_ACTIVITY_PARAMS = {
    types: [],
    days: 'all'
};

class BaseAccount {
}

class BankAccount extends BaseAccount {
}

/**
 * error item structure for server error responses.
 */
class ErrorItem {
}

/**
 * server response wrapper for error responses.
 * @description covers validation as well as server errors
 */
class ErrorResponseWrapper {
}

/**
 * structure of the metadata portion of our standard server wrapped response.
 */
class ResponseMetadata {
}

/**
 * describes validation error indexor object.
 * @description
 */
class ValidationErrorMap {
}

class DiagnosisCodeOption {
}

class PaginatedData {
}

const TypeaheadConfigDefaults = {
    noInputText: `Start typing to search...`,
    noResultsText: `Sorry, we couldn't find anything that matched...`,
    loadMoreText: `Load more`,
    loadingMoreText: `Loading more...`,
    minSearchLength: 3,
    pagingOnly: false,
    debounceTime: 750,
};

class OrdinalPipe {
    transform(int) {
        if (!int) {
            return null;
        }
        return num2text.toOrdinal(int);
    }
    static { this.ɵfac = function OrdinalPipe_Factory(t) { return new (t || OrdinalPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "ordinal", type: OrdinalPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrdinalPipe, [{
        type: Pipe,
        args: [{ name: 'ordinal' }]
    }], null, null); })();

class BirthdatePipe {
    transform(value) {
        if (!value) {
            return null;
        }
        return dayjs(value).format('MM/DD/YYYY');
    }
    static { this.ɵfac = function BirthdatePipe_Factory(t) { return new (t || BirthdatePipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "birthdate", type: BirthdatePipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BirthdatePipe, [{
        type: Pipe,
        args: [{
                name: 'birthdate'
            }]
    }], null, null); })();

class DotsPipe {
    transform(dots, reverse = false) {
        if (dots?.length > 0) {
            return reverse
                ? dots.replace(/•/g, 'X').replace(/ - /g, '-')
                : dots.replace(/X/g, '•').replace(/-/g, ' - ');
        }
        return null;
    }
    static { this.ɵfac = function DotsPipe_Factory(t) { return new (t || DotsPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "dots", type: DotsPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotsPipe, [{
        type: Pipe,
        args: [{
                name: 'dots'
            }]
    }], null, null); })();

// This pipe is effectively an alias, for backward compatibility
class SSNPipe extends DotsPipe {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵSSNPipe_BaseFactory; return function SSNPipe_Factory(t) { return (ɵSSNPipe_BaseFactory || (ɵSSNPipe_BaseFactory = i0.ɵɵgetInheritedFactory(SSNPipe)))(t || SSNPipe); }; }(); }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "ssn", type: SSNPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SSNPipe, [{
        type: Pipe,
        args: [{
                name: 'ssn'
            }]
    }], null, null); })();

class LastFourPipe {
    transform(text) {
        if (text && text.length > 0) {
            return text.replace(/\w(?=\w{4})/g, '•').replace(/-/g, ' - ');
        }
        return null;
    }
    static { this.ɵfac = function LastFourPipe_Factory(t) { return new (t || LastFourPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "lastFour", type: LastFourPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LastFourPipe, [{
        type: Pipe,
        args: [{
                name: 'lastFour'
            }]
    }], null, null); })();

class AAnPipe {
    transform(str) {
        if (str && str.length > 0) {
            return `${/^[aeiou]/i.test(str) ? 'an' : 'a'} ${str}`;
        }
        return null;
    }
    static { this.ɵfac = function AAnPipe_Factory(t) { return new (t || AAnPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "aan", type: AAnPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AAnPipe, [{
        type: Pipe,
        args: [{
                name: 'aan'
            }]
    }], null, null); })();

class KeysPipe {
    transform(value) {
        if (value) {
            return Object.keys(value);
        }
        return null;
    }
    static { this.ɵfac = function KeysPipe_Factory(t) { return new (t || KeysPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "keys", type: KeysPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(KeysPipe, [{
        type: Pipe,
        args: [{
                name: 'keys'
            }]
    }], null, null); })();

class ValuesPipe {
    transform(value) {
        if (value) {
            let keys = Object.keys(value), values = [];
            // Loop through the object, pushing values to the return array
            keys.forEach((key) => {
                values.push(value[key]);
            });
            return values;
        }
        return null;
    }
    static { this.ɵfac = function ValuesPipe_Factory(t) { return new (t || ValuesPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "values", type: ValuesPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ValuesPipe, [{
        type: Pipe,
        args: [{
                name: 'values'
            }]
    }], null, null); })();

class EntriesPipe {
    transform(value) {
        if (value) {
            return Object.entries(value).map(e => ({ key: e[0], value: e[1] }));
        }
        return null;
    }
    static { this.ɵfac = function EntriesPipe_Factory(t) { return new (t || EntriesPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "entries", type: EntriesPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EntriesPipe, [{
        type: Pipe,
        args: [{
                name: 'entries'
            }]
    }], null, null); })();

class ListPipe {
    transform(value) {
        if (value && value.length > 0) {
            return value.join(', ')
                .replace(/,(?!.*,)/gmi, value.length === 2
                ? ' and'
                : ', and');
        }
        return null;
    }
    static { this.ɵfac = function ListPipe_Factory(t) { return new (t || ListPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "list", type: ListPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ListPipe, [{
        type: Pipe,
        args: [{
                name: 'list'
            }]
    }], null, null); })();

class EmploymentStatusPipe {
    transform(value) {
        return EmploymentStatusText[value];
    }
    static { this.ɵfac = function EmploymentStatusPipe_Factory(t) { return new (t || EmploymentStatusPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "employmentStatus", type: EmploymentStatusPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EmploymentStatusPipe, [{
        type: Pipe,
        args: [{
                name: 'employmentStatus'
            }]
    }], null, null); })();

const states = {
    AL: `Alabama`,
    AK: `Alaska`,
    AZ: `Arizona`,
    AR: `Arkansas`,
    CA: `California`,
    CO: `Colorado`,
    CT: `Connecticut`,
    DE: `Delaware`,
    DC: `District of Columbia`,
    FL: `Florida`,
    GA: `Georgia`,
    HI: `Hawaii`,
    ID: `Idaho`,
    IL: `Illinois`,
    IN: `Indiana`,
    IA: `Iowa`,
    KS: `Kansas`,
    KY: `Kentucky`,
    LA: `Louisiana`,
    ME: `Maine`,
    MD: `Maryland`,
    MA: `Massachusetts`,
    MI: `Michigan`,
    MN: `Minnesota`,
    MS: `Mississippi`,
    MO: `Missouri`,
    MT: `Montana`,
    NE: `Nebraska`,
    NV: `Nevada`,
    NH: `New Hampshire`,
    NJ: `New Jersey`,
    NM: `New Mexico`,
    NY: `New York`,
    NC: `North Carolina`,
    ND: `North Dakota`,
    OH: `Ohio`,
    OK: `Oklahoma`,
    OR: `Oregon`,
    PA: `Pennsylvania`,
    RI: `Rhode Island`,
    SC: `South Carolina`,
    SD: `South Dakota`,
    TN: `Tennessee`,
    TX: `Texas`,
    UT: `Utah`,
    VT: `Vermont`,
    VA: `Virginia`,
    WA: `Washington`,
    WV: `West Virginia`,
    WI: `Wisconsin`,
    WY: `Wyoming`,
};
const militaryRegions = {
    AA: `Armed Forces - AA`,
    AE: `Armed Forces - AE`,
    AP: `Armed Forces - AP`,
};
const territories = {
    GU: 'Guam',
    MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico',
    VI: 'Virgin Islands',
};
const statesWithMilitary = {
    ...states,
    ...militaryRegions,
};
const statesWithTerritories = {
    ...states,
    ...territories,
};
const statesWithMilitaryAndTerritories = {
    ...states,
    ...territories,
    ...militaryRegions,
};

const countries = {
    AF: `Afghanistan`,
    AX: `\u00c5land Islands`,
    AL: `Albania`,
    DZ: `Algeria`,
    AS: `American Samoa`,
    AD: `Andorra`,
    AO: `Angola`,
    AI: `Anguilla`,
    AQ: `Antarctica`,
    AG: `Antigua and Barbuda`,
    AR: `Argentina`,
    AM: `Armenia`,
    AW: `Aruba`,
    AU: `Australia`,
    AT: `Austria`,
    AZ: `Azerbaijan`,
    BS: `Bahamas`,
    BH: `Bahrain`,
    BD: `Bangladesh`,
    BB: `Barbados`,
    BY: `Belarus`,
    BE: `Belgium`,
    BZ: `Belize`,
    BJ: `Benin`,
    BM: `Bermuda`,
    BT: `Bhutan`,
    BO: `Bolivia, Plurinational State of`,
    BQ: `Bonaire, Sint Eustatius and Saba`,
    BA: `Bosnia and Herzegovina`,
    BW: `Botswana`,
    BV: `Bouvet Island`,
    BR: `Brazil`,
    IO: `British Indian Ocean Territory`,
    BN: `Brunei Darussalam`,
    BG: `Bulgaria`,
    BF: `Burkina Faso`,
    BI: `Burundi`,
    KH: `Cambodia`,
    CM: `Cameroon`,
    CA: `Canada`,
    CV: `Cape Verde`,
    KY: `Cayman Islands`,
    CF: `Central African Republic`,
    TD: `Chad`,
    CL: `Chile`,
    CN: `China`,
    CX: `Christmas Island`,
    CC: `Cocos (Keeling) Islands`,
    CO: `Colombia`,
    KM: `Comoros`,
    CG: `Congo`,
    CD: `Congo, the Democratic Republic of the`,
    CK: `Cook Islands`,
    CR: `Costa Rica`,
    CI: `C\u00f4te d'Ivoire`,
    HR: `Croatia`,
    CU: `Cuba`,
    CW: `Cura\u00e7ao`,
    CY: `Cyprus`,
    CZ: `Czech Republic`,
    DK: `Denmark`,
    DJ: `Djibouti`,
    DM: `Dominica`,
    DO: `Dominican Republic`,
    EC: `Ecuador`,
    EG: `Egypt`,
    SV: `El Salvador`,
    GQ: `Equatorial Guinea`,
    ER: `Eritrea`,
    EE: `Estonia`,
    ET: `Ethiopia`,
    FK: `Falkland Islands (Malvinas)`,
    FO: `Faroe Islands`,
    FJ: `Fiji`,
    FI: `Finland`,
    FR: `France`,
    GF: `French Guiana`,
    PF: `French Polynesia`,
    TF: `French Southern Territories`,
    GA: `Gabon`,
    GM: `Gambia`,
    GE: `Georgia`,
    DE: `Germany`,
    GH: `Ghana`,
    GI: `Gibraltar`,
    GR: `Greece`,
    GL: `Greenland`,
    GD: `Grenada`,
    GP: `Guadeloupe`,
    GU: `Guam`,
    GT: `Guatemala`,
    GG: `Guernsey`,
    GN: `Guinea`,
    GW: `Guinea-Bissau`,
    GY: `Guyana`,
    HT: `Haiti`,
    HM: `Heard Island and McDonald Islands`,
    VA: `Holy See (Vatican City State)`,
    HN: `Honduras`,
    HK: `Hong Kong`,
    HU: `Hungary`,
    IS: `Iceland`,
    IN: `India`,
    ID: `Indonesia`,
    IR: `Iran, Islamic Republic of`,
    IQ: `Iraq`,
    IE: `Ireland`,
    IM: `Isle of Man`,
    IL: `Israel`,
    IT: `Italy`,
    JM: `Jamaica`,
    JP: `Japan`,
    JE: `Jersey`,
    JO: `Jordan`,
    KZ: `Kazakhstan`,
    KE: `Kenya`,
    KI: `Kiribati`,
    KP: `Korea, Democratic People's Republic of`,
    KR: `Korea, Republic of`,
    KW: `Kuwait`,
    KG: `Kyrgyzstan`,
    LA: `Lao People's Democratic Republic`,
    LV: `Latvia`,
    LB: `Lebanon`,
    LS: `Lesotho`,
    LR: `Liberia`,
    LY: `Libya`,
    LI: `Liechtenstein`,
    LT: `Lithuania`,
    LU: `Luxembourg`,
    MO: `Macao`,
    MK: `Macedonia, the Former Yugoslav Republic of`,
    MG: `Madagascar`,
    MW: `Malawi`,
    MY: `Malaysia`,
    MV: `Maldives`,
    ML: `Mali`,
    MT: `Malta`,
    MH: `Marshall Islands`,
    MQ: `Martinique`,
    MR: `Mauritania`,
    MU: `Mauritius`,
    YT: `Mayotte`,
    MX: `Mexico`,
    FM: `Micronesia, Federated States of`,
    MD: `Moldova, Republic of`,
    MC: `Monaco`,
    MN: `Mongolia`,
    ME: `Montenegro`,
    MS: `Montserrat`,
    MA: `Morocco`,
    MZ: `Mozambique`,
    MM: `Myanmar`,
    NA: `Namibia`,
    NR: `Nauru`,
    NP: `Nepal`,
    NL: `Netherlands`,
    NC: `New Caledonia`,
    NZ: `New Zealand`,
    NI: `Nicaragua`,
    NE: `Niger`,
    NG: `Nigeria`,
    NU: `Niue`,
    NF: `Norfolk Island`,
    MP: `Northern Mariana Islands`,
    NO: `Norway`,
    OM: `Oman`,
    PK: `Pakistan`,
    PW: `Palau`,
    PS: `Palestine, State of`,
    PA: `Panama`,
    PG: `Papua New Guinea`,
    PY: `Paraguay`,
    PE: `Peru`,
    PH: `Philippines`,
    PN: `Pitcairn`,
    PL: `Poland`,
    PT: `Portugal`,
    PR: `Puerto Rico`,
    QA: `Qatar`,
    RE: `R\u00e9union`,
    RO: `Romania`,
    RU: `Russian Federation`,
    RW: `Rwanda`,
    BL: `Saint Barth\u00e9lemy`,
    SH: `Saint Helena, Ascension and Tristan da Cunha`,
    KN: `Saint Kitts and Nevis`,
    LC: `Saint Lucia`,
    MF: `Saint Martin (French part)`,
    PM: `Saint Pierre and Miquelon`,
    VC: `Saint Vincent and the Grenadines`,
    WS: `Samoa`,
    SM: `San Marino`,
    ST: `Sao Tome and Principe`,
    SA: `Saudi Arabia`,
    SN: `Senegal`,
    RS: `Serbia`,
    SC: `Seychelles`,
    SL: `Sierra Leone`,
    SG: `Singapore`,
    SX: `Sint Maarten (Dutch part)`,
    SK: `Slovakia`,
    SI: `Slovenia`,
    SB: `Solomon Islands`,
    SO: `Somalia`,
    ZA: `South Africa`,
    GS: `South Georgia and the South Sandwich Islands`,
    SS: `South Sudan`,
    ES: `Spain`,
    LK: `Sri Lanka`,
    SD: `Sudan`,
    SR: `Suriname`,
    SJ: `Svalbard and Jan Mayen`,
    SZ: `Swaziland`,
    SE: `Sweden`,
    CH: `Switzerland`,
    SY: `Syrian Arab Republic`,
    TW: `Taiwan, Province of China`,
    TJ: `Tajikistan`,
    TZ: `Tanzania, United Republic of`,
    TH: `Thailand`,
    TL: `Timor-Leste`,
    TG: `Togo`,
    TK: `Tokelau`,
    TO: `Tonga`,
    TT: `Trinidad and Tobago`,
    TN: `Tunisia`,
    TR: `Turkey`,
    TM: `Turkmenistan`,
    TC: `Turks and Caicos Islands`,
    TV: `Tuvalu`,
    UG: `Uganda`,
    UA: `Ukraine`,
    AE: `United Arab Emirates`,
    GB: `United Kingdom`,
    US: `United States`,
    UM: `United States Minor Outlying Islands`,
    UY: `Uruguay`,
    UZ: `Uzbekistan`,
    VU: `Vanuatu`,
    VE: `Venezuela, Bolivarian Republic of`,
    VN: `Viet Nam`,
    VG: `Virgin Islands, British`,
    VI: `Virgin Islands, U.S.`,
    WF: `Wallis and Futuna`,
    EH: `Western Sahara`,
    YE: `Yemen`,
    ZM: `Zambia`,
    ZW: `Zimbabwe`,
};

class AddressPipe {
    transform(a, multiline = false, showCountry = false) {
        if (!a) {
            return '';
        }
        let address = [];
        if (a.line1) {
            if (multiline) {
                address.push(`${a.line1}<br />`);
            }
            else {
                address.push(`${a.line1},`);
            }
        }
        if (a.line2) {
            if (multiline) {
                address.push(`${a.line2}<br />`);
            }
            else {
                address.push(`${a.line2},`);
            }
        }
        if (a.line3) {
            if (multiline) {
                address.push(`${a.line3}<br />`);
            }
            else {
                address.push(`${a.line3},`);
            }
        }
        if (a.city) {
            address.push(`${a.city},`);
        }
        if (a.stateCode) {
            address.push(a.stateCode);
        }
        if (a.postalCode) {
            address.push(a.postalCode);
        }
        if (a.countryCode && showCountry) {
            let country = countries[a.countryCode] || a.countryCode;
            if (multiline) {
                address.push(`<br /> ${country}`);
            }
            else {
                address.push(`${country}`);
            }
        }
        return address.join(' ');
    }
    static { this.ɵfac = function AddressPipe_Factory(t) { return new (t || AddressPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "address", type: AddressPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AddressPipe, [{
        type: Pipe,
        args: [{
                name: 'address'
            }]
    }], null, null); })();

class BankAccountDescPipe {
    transform(value, showNickname = true) {
        if (value) {
            if (value.nickname && showNickname) {
                return `${value.nickname}<br />${new DotsPipe().transform(value.accountNumber)}`;
            }
            let title = (showNickname) ? `${value.title}<br />` : '';
            // Capitalize the first letter of the account type string
            let type = value.type.charAt(0).toUpperCase() + value.type.slice(1);
            return `${title}${value.name}'s ${type} Account (${new DotsPipe().transform(value.accountNumber)})`;
        }
        return null;
    }
    static { this.ɵfac = function BankAccountDescPipe_Factory(t) { return new (t || BankAccountDescPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "bankAccountDesc", type: BankAccountDescPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BankAccountDescPipe, [{
        type: Pipe,
        args: [{
                name: 'bankAccountDesc'
            }]
    }], null, null); })();

class OccupationPipe {
    transform(occupation, type) {
        let formats = {
            text: occupation,
            value: occupation,
            other: null
        };
        if (occupation) {
            if (occupation.indexOf('OTH-') === 0) {
                formats.value = 'Other';
                formats.text = formats.other = occupation.substring(4);
            }
        }
        return type ? formats[type] : formats;
    }
    static { this.ɵfac = function OccupationPipe_Factory(t) { return new (t || OccupationPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "occupation", type: OccupationPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OccupationPipe, [{
        type: Pipe,
        args: [{
                name: 'occupation'
            }]
    }], null, null); })();

class MomentPipe {
    transform(value, format) {
        if (value === null) {
            return null;
        }
        return dayjs(value).format(format);
    }
    static { this.ɵfac = function MomentPipe_Factory(t) { return new (t || MomentPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "moment", type: MomentPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MomentPipe, [{
        type: Pipe,
        args: [{
                name: 'moment'
            }]
    }], null, null); })();

class PluralPipe {
    transform(text, count, prepend = false) {
        if (text && count) {
            return pluralize(text, count, prepend);
        }
        return text;
    }
    static { this.ɵfac = function PluralPipe_Factory(t) { return new (t || PluralPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "plural", type: PluralPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PluralPipe, [{
        type: Pipe,
        args: [{
                name: 'plural'
            }]
    }], null, null); })();

class BlankPipe {
    constructor() {
        // So Angular is dumb and won't let us use `&mdash;` as a default string, so we use the character directly -- @ytilis
        this.defaultReplacement = '—';
    }
    static { this.blankValues = [
        0,
        '0',
        '$0',
        '$0.00',
        '',
        null,
        undefined,
    ]; }
    transform(text, replacement = this.defaultReplacement) {
        return BlankPipe.blankValues.includes(text) ? replacement : text;
    }
    static { this.ɵfac = function BlankPipe_Factory(t) { return new (t || BlankPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "blank", type: BlankPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BlankPipe, [{
        type: Pipe,
        args: [{
                name: 'blank'
            }]
    }], null, null); })();

/*
 * Convert bytes into largest possible unit.
 * Takes an precision argument that defaults to 2.
 * Usage:
 *   bytes | fileSize:precision
 * Example:
 *   {{ 1024 | fileSize}}
 *   formats to: 1 KB
 *
 * OR
 *
 * Converts unit string to bytes
 * Usage:
 *    sizeInUnits | fileSize
 * Example:
 *    {{ 10MB | fileSize }}
 *    formats to: 10485760
 *
 */
class FileSizePipe {
    constructor() {
        this.units = [
            'bytes',
            'KB',
            'MB',
            'GB',
            'TB',
            'PB'
        ];
    }
    transform(input = 0, precision = 2) {
        if (!input) {
            return null;
        }
        if (typeof input === 'number') {
            return this.toUnits(input, precision);
        }
        else {
            return this.toBytes(input);
        }
    }
    toUnits(bytes = 0, precision = 2) {
        // Abort if it's not  finite number
        if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) {
            return null;
        }
        let unit = 0;
        while (bytes >= 1024) {
            bytes /= 1024;
            unit++;
        }
        // Trims the precision down and chops off trailing 0s with parseFloat
        bytes = parseFloat(bytes.toFixed(+precision));
        return `${bytes}${this.units[unit]}`;
    }
    toBytes(input) {
        let unitString = input.match(/(\d*.?\d+)\s?(\w+)/);
        // Abort if the regex doesn't match
        if (!unitString) {
            return null;
        }
        // Fetch the index of the units in our array
        let index = this.units.indexOf(unitString[2]);
        // Abort if the units aren't in our list
        if (!index) {
            return null;
        }
        return parseFloat(unitString[1]) * Math.pow(1024, index);
    }
    static { this.ɵfac = function FileSizePipe_Factory(t) { return new (t || FileSizePipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "fileSize", type: FileSizePipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FileSizePipe, [{
        type: Pipe,
        args: [{
                name: 'fileSize'
            }]
    }], null, null); })();

class CardNumberPipe {
    transform(value) {
        if (value) {
            return new DotsPipe().transform('XXXX XXXX XXXX ' + value.cardLast4);
        }
        return null;
    }
    static { this.ɵfac = function CardNumberPipe_Factory(t) { return new (t || CardNumberPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "cardNumber", type: CardNumberPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CardNumberPipe, [{
        type: Pipe,
        args: [{
                name: 'cardNumber'
            }]
    }], null, null); })();

const defaultOptions = {
    prefixForNegative: 'minus',
    shouldBeTitleCase: false,
    shouldExcludeCommas: false,
    shouldFormatForLegal: false,
    shouldInsertAnd: false,
};
class NamedIntegerPipe {
    formatReturn(text, valueString, config) {
        let formattedText = text;
        if (valueString[0] === '-') {
            formattedText = `${config.prefixForNegative} ${formattedText}`;
        }
        if (config.shouldExcludeCommas) {
            formattedText = formattedText.replace(/,/g, '');
        }
        if (config.shouldBeTitleCase) {
            formattedText = TextUtils.toTitleCase(formattedText);
        }
        if (config.shouldInsertAnd) {
            const words = formattedText.split(' ');
            if (words.length > 1) {
                formattedText = [...words.slice(0, words.length - 1), 'and', words[words.length - 1]].join(' ');
            }
        }
        if (config.shouldFormatForLegal) {
            formattedText = `${formattedText} (${valueString})`;
        }
        return formattedText;
    }
    transform(value, options) {
        const config = Object.assign({}, defaultOptions, options);
        const valueString = value.toString().trim();
        // Return stringified input if input is empty or non-integer.
        if (!valueString.length || valueString.includes('.')) {
            return valueString;
        }
        const numericString = valueString.replace(/\D/g, '');
        // Return stringified input if numeric string is empty.
        if (!numericString.length) {
            return valueString;
        }
        const noCommasString = valueString.replace(/-|,/g, '');
        // Return stringified input if input contains non-numerics other than commas.
        if (numericString !== noCommasString) {
            return valueString;
        }
        const num = Number(numericString);
        // Return zero when applicable.
        if (!num) {
            return this.formatReturn('zero', valueString, config);
        }
        return this.formatReturn(num2text.toWords(num), valueString, config);
    }
    static { this.ɵfac = function NamedIntegerPipe_Factory(t) { return new (t || NamedIntegerPipe)(); }; }
    static { this.ɵpipe = /*@__PURE__*/ i0.ɵɵdefinePipe({ name: "namedInteger", type: NamedIntegerPipe, pure: true }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NamedIntegerPipe, [{
        type: Pipe,
        args: [{ name: 'namedInteger' }]
    }], null, null); })();

class PageFocusService {
    get isServer() {
        return isPlatformServer(this.platformId);
    }
    constructor(windowRef, _ngZone, platformId) {
        this.windowRef = windowRef;
        this._ngZone = _ngZone;
        this.platformId = platformId;
        this.firstItemToFocus = '[logo] a';
    }
    setFocusOnElement(cssSelector) {
        if (this.isServer) {
            return;
        }
        let element = this.windowRef.nativeWindow.document.querySelector(cssSelector);
        if (element) {
            element.tabIndex = -1;
            element.focus();
        }
    }
    setFocusForPageChange(retryCount = 0) {
        if (this.isServer) {
            return;
        }
        let firstItemToFocus = this.windowRef.nativeWindow.document.querySelector(this.firstItemToFocus);
        if (!firstItemToFocus) {
            if (retryCount < 20) {
                setTimeout(() => this.setFocusForPageChange(retryCount + 1), 100);
            }
            else {
                isDevMode() && console.warn(`couldn't set the focus!`);
            }
        }
        else {
            setTimeout(() => {
                firstItemToFocus.focus();
            }, 500);
        }
    }
    // this is just so we can mock in the tests (activeElement is readonly)
    getActiveElement() {
        return this.windowRef.nativeWindow.document.activeElement;
    }
    resetFocus() {
        if (this.isServer) {
            return;
        }
        this.scrollToTop();
        this.setFocusForPageChange();
    }
    skipToMainContent() {
        if (this.isServer) {
            return;
        }
        this.setFocusOnElement('#main-content');
    }
    blockExternalFocus(_element, externalContainerElements, externalTabbableElements) {
        if (this.isServer) {
            return;
        }
        let parent = _element.parentNode;
        while (parent) {
            let children = parent.children;
            for (let i = 0; i < children.length; i++) {
                let child = children[i];
                if (!child.contains(_element)) {
                    if (!child.hasAttribute('aria-hidden')
                        && child.tagName !== 'APP-TAKE-OVER-MODAL') {
                        externalContainerElements.push(child);
                        child.setAttribute('aria-hidden', true);
                        child.tabIndex = -1;
                    }
                }
            }
            if (parent.parentNode === document.body) {
                parent = null;
            }
            else {
                parent = parent.parentNode;
            }
        }
        let tabbables = tabbable(document.body);
        tabbables.forEach((element, index) => {
            if (_element.contains(element)) {
                return true;
            }
            externalTabbableElements.push(element);
            element.tabIndex = -1;
            element.setAttribute('aria-hidden', 'true');
        });
    }
    unblockExternalFocus(externalContainerElements, externalTabbableElements) {
        externalContainerElements.forEach((element, index) => {
            element.removeAttribute('tabIndex');
            element.removeAttribute('aria-hidden');
        });
        externalTabbableElements.forEach((element, index) => {
            element.tabIndex = 0;
            element.removeAttribute('aria-hidden');
        });
    }
    scrollToTop() {
        if (this.isServer) {
            return;
        }
        const body = this.windowRef.nativeWindow.document.querySelector('body');
        if (body) {
            if (body.scrollIntoView) {
                body.scrollIntoView(true);
            }
            body.scrollTop = 0;
        }
        const pageWrapper = this.windowRef.nativeWindow.document.querySelector('.page-content');
        if (pageWrapper && this.windowRef.nativeWindow.getComputedStyle(pageWrapper).overflow !== 'visible') {
            if (pageWrapper.scrollIntoView && pageWrapper.scrollIntoView.call) {
                pageWrapper.scrollIntoView(true);
            }
            this._ngZone.runOutsideAngular(() => {
                setTimeout(() => {
                    pageWrapper.scrollTop = 0;
                }, 1);
            });
        }
    }
    static { this.ɵfac = function PageFocusService_Factory(t) { return new (t || PageFocusService)(i0.ɵɵinject(WindowRefService), i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(PLATFORM_ID)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: PageFocusService, factory: PageFocusService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PageFocusService, [{
        type: Injectable
    }], function () { return [{ type: WindowRefService }, { type: i0.NgZone }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

class MockPageFocusService {
    loading() { }
    scrollToTop() { }
    blockExternalFocus(_element, containers, tabbable) { }
    unblockExternalFocus(containers, tabbable) { }
    static { this.ɵfac = function MockPageFocusService_Factory(t) { return new (t || MockPageFocusService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockPageFocusService, factory: MockPageFocusService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockPageFocusService, [{
        type: Injectable
    }], null, null); })();

class MockWindowRefService {
    constructor() {
        this.events = {
            resize: of({}),
        };
        this.nativeWindow = {
            scrollTo: function () { },
            addEventListener: function () { },
            removeEventListener: function () { },
            requestAnimationFrame: function () { },
            getComputedStyle: function () {
                return {
                    position: ''
                };
            },
            navigator: {
                userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_12_6) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/65.0.3325.146 Safari/537.36'
            },
            location: {
                reload: function () { }
            },
            document: {
                activeElement: {
                    tagName: '',
                    blur: function () { }
                },
                querySelector: function () { }
            }
        };
    }
    static { this.ɵfac = function MockWindowRefService_Factory(t) { return new (t || MockWindowRefService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockWindowRefService, factory: MockWindowRefService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockWindowRefService, [{
        type: Injectable
    }], null, null); })();

class StorageService {
    constructor(_window, platformId) {
        this._window = _window;
        this.platformId = platformId;
        this.useLocalStorage = false;
        this.memoryStore = {};
        if (isPlatformBrowser(this.platformId)) {
            this.localStorage = this._window.nativeWindow.localStorage;
        }
        this.useLocalStorage = this.test();
    }
    test() {
        // Safari is an awful browser. In Private Browsing mode, instead of removing the localStorage object,
        // it just sets the quota to 0, which throws an exception when you try to use it, but still flags it as available.
        // So we have to test it before using it to make sure it ACTUALLY works - YT
        if (typeof this.localStorage === 'object' && Storage !== void (0)) {
            try {
                this.localStorage.setItem('localStorage', 'test');
                this.localStorage.removeItem('localStorage');
                return true;
            }
            catch (e) {
                return false;
            }
        }
    }
    get(key, namespace) {
        let value;
        let stringValue;
        if (namespace) {
            key = `${namespace}.${key}`;
        }
        if (this.useLocalStorage) {
            stringValue = this.localStorage.getItem(key);
        }
        else {
            stringValue = this.memoryStore[key];
        }
        // Parse the stringified JSON, or return the raw value if we can't
        try {
            value = JSON.parse(stringValue);
        }
        catch (e) {
            value = stringValue;
        }
        return value;
    }
    set(key, value, namespace) {
        let stringValue;
        // Stringify, or pass the raw value if we can't
        try {
            stringValue = JSON.stringify(value);
        }
        catch (e) {
            stringValue = value;
        }
        if (namespace) {
            key = `${namespace}.${key}`;
        }
        if (this.useLocalStorage) {
            this.localStorage.setItem(key, stringValue);
        }
        else {
            this.memoryStore[key] = stringValue;
        }
    }
    remove(key, namespace) {
        if (namespace) {
            key = `${namespace}.${key}`;
        }
        if (this.useLocalStorage) {
            this.localStorage.removeItem(key);
        }
        else {
            delete this.memoryStore[key];
        }
    }
    clear(namespace) {
        if (namespace) {
            namespace = `${namespace}.`;
            // If we're clearing a namespace, loop over the entries to find the ones we want, and remove them
            let remove = [];
            let length = this.useLocalStorage ? this.localStorage.length : Object.keys(this.memoryStore).length;
            // Iterate over localStorage and save the keys we want to remove
            for (let i = 0; i < length; i++) {
                let entry = this.useLocalStorage ? this.localStorage.key(i) : this.memoryStore[i];
                if (entry.indexOf(namespace) === 0) {
                    remove.push(entry);
                }
            }
            // Iterate over our list of arrays we want removed
            for (let i = 0; i < remove.length; i++) {
                if (this.useLocalStorage) {
                    this.localStorage.removeItem(remove[i]);
                }
                else {
                    delete this.memoryStore[remove[i]];
                }
            }
        }
        else {
            // If there's no namespace, we just clear everything
            if (this.useLocalStorage) {
                this.localStorage.clear();
            }
            else {
                this.memoryStore = {};
            }
        }
    }
    static { this.ɵfac = function StorageService_Factory(t) { return new (t || StorageService)(i0.ɵɵinject(WindowRefService), i0.ɵɵinject(PLATFORM_ID)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: StorageService, factory: StorageService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StorageService, [{
        type: Injectable
    }], function () { return [{ type: WindowRefService }, { type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }]; }, null); })();

var TakeoverType;
(function (TakeoverType) {
    TakeoverType["Confirmation"] = "confirmation";
    TakeoverType["Error"] = "error";
    TakeoverType["Timeout"] = "timeout";
    TakeoverType["Pending"] = "pending";
    TakeoverType["NoEdits"] = "no-edits";
})(TakeoverType || (TakeoverType = {}));
const DEFAULT_TAKEOVER_OPTIONS = {
    link: 'Continue',
    type: TakeoverType.Confirmation,
    closeOnClick: true,
};
/**
 * Async modal dialog service
 * DialogService makes this app easier to test by faking this service.
 */
class DialogService {
    constructor(router, window) {
        this.router = router;
        this.window = window;
        this._closed = new Subject();
        this._disableBounceScroll = new Subject();
        this._confirmationSubject = new Subject();
        this.confirmation = this._confirmationSubject.asObservable();
        this.disableBounceScroll = this._disableBounceScroll.asObservable();
        this.closed = this._closed.asObservable();
        this.eventListener = (event) => {
            switch (event.keyCode) {
                case KeyCodes.TAB:
                    event.preventDefault();
                    break;
                case KeyCodes.ENTER:
                    event.preventDefault();
                    this.close();
                    break;
                case KeyCodes.ESC:
                    event.preventDefault();
                    this.close();
                    break;
            }
        };
    }
    /**
     * Ask user to confirm an action. `message` explains the action and choices.
     * Returns promise resolving to `true`=confirm or `false`=cancel
     */
    confirm(message) {
        return new Promise((resolve, reject) => resolve(this.window.nativeWindow.confirm(message || 'Is it OK?')));
    }
    close(force = false) {
        this.window.nativeWindow.removeEventListener('keydown', this.eventListener);
        if (!force && this.takeoverOptions && this.takeoverOptions.closeOnClick) {
            this._closed.next(this.takeoverOptions && 'navigate' in this.takeoverOptions);
        }
        if (this.closedDeferred) {
            this.closedDeferred.resolve(true);
            this.closedDeferred = null;
        }
        if (this.takeoverOptions && this.takeoverOptions.navigate) {
            this.router.navigate(this.takeoverOptions.navigate);
        }
        this.isOpen = false;
    }
    showConfirmation(params) {
        if (this.isOpen) {
            this.close();
        }
        params = Object.assign({}, DEFAULT_TAKEOVER_OPTIONS, params);
        this.takeoverOptions = params;
        this._confirmationSubject.next(params);
        this.window.nativeWindow.addEventListener('keydown', this.eventListener);
        this.closedDeferred = new Deferred();
        this.isOpen = true;
        return this.closedDeferred.promise;
    }
    setDisableBounceScroll(scroll) {
        this._disableBounceScroll.next(scroll);
    }
    static { this.ɵfac = function DialogService_Factory(t) { return new (t || DialogService)(i0.ɵɵinject(i1$1.Router), i0.ɵɵinject(WindowRefService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DialogService, factory: DialogService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogService, [{
        type: Injectable
    }], function () { return [{ type: i1$1.Router }, { type: WindowRefService }]; }, null); })();

const DIAGNOSIS_CODES = [
    {
        code: 1,
        name: 'Developmental Disorder',
        description: 'Autistic Spectrum Disorder, Asperger\'s Disorder, Developmental Delays ' +
            'and Learning Disabilities'
    },
    {
        code: 2,
        name: 'Intellectual Disability',
        description: 'Mild, moderate, or severe intellectual disability'
    },
    {
        code: 3,
        name: 'Psychiatric Disorder',
        description: 'Schizophrenia, Major depressive disorder, ' +
            'Post-traumatic stress disorder (PTSD), Anorexia nervosa, ' +
            'Attention deficit/hyperactivity disorder (AD/HD) and Bipolar disorder'
    },
    {
        code: 4,
        name: 'Nervous Disorder',
        description: 'Blindness, Deafness, Cerebral Palsy, Muscular Dystrophy, Spina Bifida, ' +
            'Juvenile-onset Huntington\'s disease, Multiple sclerosis, Severe sensorineural ' +
            'hearing loss and Congenital cataracts'
    },
    {
        code: 5,
        name: 'Congenital Anomaly',
        description: 'Chromosomal abnormalities: Down Syndrome, Osteogenesis imperfecta, ' +
            'Xeroderma pigmentosum, Spinal muscular atrophy, Fragile X syndrome and Edwards syndrome'
    },
    {
        code: 6,
        name: 'Respiratory Disorder',
        description: 'Cystic Fibrosis'
    },
    {
        code: 7,
        name: 'Other',
        description: 'Anything not listed under codes 1-6 and Tetralogy of Fallot, ' +
            'Hypoplastic left heart syndrome, End-stage liver disease, ' +
            'Juvenile-onset rheumatoid arthritis, Sickle cell disease and Hemophilia'
    }
];

class DiagnosisCodeOptionsService {
    constructor() {
        this.options = DIAGNOSIS_CODES;
        this.createMap();
    }
    getOptions() {
        return this.options;
    }
    getByCode(code) {
        if (this.map.has(code)) {
            return this.map.get(code);
        }
        return null;
    }
    getDescription(code) {
        let diagnosisCode = this.getByCode(code);
        return `Code ${code} ${diagnosisCode.name}`;
    }
    createMap() {
        this.map = new Map();
        for (let option of this.options) {
            this.map.set(option.code, option);
        }
    }
    static { this.ɵfac = function DiagnosisCodeOptionsService_Factory(t) { return new (t || DiagnosisCodeOptionsService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: DiagnosisCodeOptionsService, factory: DiagnosisCodeOptionsService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DiagnosisCodeOptionsService, [{
        type: Injectable
    }], function () { return []; }, null); })();

var CanvasTextAlign;
(function (CanvasTextAlign) {
    CanvasTextAlign["Start"] = "start";
    CanvasTextAlign["End"] = "end";
    CanvasTextAlign["Left"] = "left";
    CanvasTextAlign["Center"] = "center";
    CanvasTextAlign["Right"] = "right";
})(CanvasTextAlign || (CanvasTextAlign = {}));
var CanvasGlobalComposition;
(function (CanvasGlobalComposition) {
    CanvasGlobalComposition["SourceOver"] = "source-over";
    CanvasGlobalComposition["SourceIn"] = "source-in";
    CanvasGlobalComposition["SourceOut"] = "source-out";
    CanvasGlobalComposition["SourceAtop"] = "source-atop";
    CanvasGlobalComposition["DestinationOver"] = "destination-over";
    CanvasGlobalComposition["DestinationIn"] = "destination-in";
    CanvasGlobalComposition["DestinationOut"] = "destination-out";
    CanvasGlobalComposition["DestinationAtop"] = "destination-atop";
    CanvasGlobalComposition["Lighter"] = "lighter";
    CanvasGlobalComposition["Copy"] = "copy";
    CanvasGlobalComposition["Xor"] = "xor";
    CanvasGlobalComposition["Multiply"] = "multiply";
    CanvasGlobalComposition["Screen"] = "screen";
    CanvasGlobalComposition["Overlay"] = "overlay";
    CanvasGlobalComposition["Darken"] = "darken";
    CanvasGlobalComposition["Lighten"] = "lighten";
    CanvasGlobalComposition["ColorDodge"] = "color-dodge";
    CanvasGlobalComposition["ColorBurn"] = "color-burn";
    CanvasGlobalComposition["HardLight"] = "hard-light";
    CanvasGlobalComposition["SoftLight"] = "soft-light";
    CanvasGlobalComposition["Difference"] = "difference";
    CanvasGlobalComposition["Exclusion"] = "exclusion";
    CanvasGlobalComposition["Hue"] = "hue";
    CanvasGlobalComposition["Saturation"] = "saturation";
    CanvasGlobalComposition["Color"] = "color";
    CanvasGlobalComposition["Luminosity"] = "luminosity";
})(CanvasGlobalComposition || (CanvasGlobalComposition = {}));
class CanvasService {
    constructor(rendererFactory) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    static scaleImageWidth(img, height) {
        return height * img.width / img.height;
    }
    static scaleImageHeight(img, width) {
        return width * img.height / img.width;
    }
    createCanvas(w = 400, h = 400, ratio) {
        ratio = ratio || this.getPixelRatio();
        this.width = w;
        this.height = h;
        this.canvasEle = this.renderer.createElement('canvas');
        this.renderer.setAttribute(this.canvasEle, 'width', `${w * ratio}`);
        this.renderer.setAttribute(this.canvasEle, 'height', `${h * ratio}`);
        this.canvas = this.canvasEle.getContext('2d');
        this.canvas.setTransform(ratio, 0, 0, ratio, 0, 0);
        return this.canvas;
    }
    getPixelRatio() {
        let ctx = this.renderer.createElement('canvas').getContext('2d'), dpr = window.devicePixelRatio || 1, bsr = ctx.webkitBackingStorePixelRatio ||
            ctx.mozBackingStorePixelRatio ||
            ctx.msBackingStorePixelRatio ||
            ctx.oBackingStorePixelRatio ||
            ctx.backingStorePixelRatio || 1;
        return dpr / bsr;
    }
    font(weight, size = '14px', family = `'Source Sans Pro'`) {
        this.canvas.font = `${weight} ${size} ${family}`;
    }
    textColumn(text = [], x = 0, y = 0, spacing = 20) {
        y = y - spacing;
        // Unfortunately, you can't get the height of text in canvas,
        // but since height is consistent in fonts, we just have to bake it into the spacing we pass in
        for (let line of text) {
            this.canvas.fillText(line, x, y += spacing);
        }
    }
    textRow(text = [], x = 0, y = 0, spacing = 20, labeledTextSize) {
        x = x - spacing;
        if (labeledTextSize) {
            this.font(600, labeledTextSize);
        }
        // Since word with is dynamic, we calculate the length to allow for even spacing between words
        let previousLine;
        let offset = 0;
        for (let line of text) {
            if (previousLine) {
                offset = this.canvas.measureText(previousLine).width;
            }
            this.canvas.fillText(line, x += offset + spacing, y);
            previousLine = line;
            if (labeledTextSize) {
                this.font(400, labeledTextSize);
            }
        }
    }
    async addImage(url) {
        return new Promise((resolve, reject) => {
            let img = this.renderer.createElement('img');
            this.renderer.setAttribute(img, 'src', url);
            img.onload = () => resolve(img);
        });
    }
    static { this.ɵfac = function CanvasService_Factory(t) { return new (t || CanvasService)(i0.ɵɵinject(i0.RendererFactory2)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CanvasService, factory: CanvasService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CanvasService, [{
        type: Injectable
    }], function () { return [{ type: i0.RendererFactory2 }]; }, null); })();

var CacheStatus;
(function (CacheStatus) {
    CacheStatus["Initial"] = "initial";
    CacheStatus["Pending"] = "pending";
    CacheStatus["Expired"] = "expired";
    CacheStatus["Normal"] = "normal";
})(CacheStatus || (CacheStatus = {}));

let globalCache = new Map();
class CacheController {
    constructor(config = {}) {
        this.status = CacheStatus.Initial;
        let newCache = new Map();
        // If it's a named cache, then it should be available in the global scope to be shared with other methods
        if (config.name) {
            // Init the cache map if it doesn't exist yet
            if (!globalCache.has(config.name)) {
                globalCache.set(config.name, newCache);
            }
            // Otherwise point to our existing cache. Multiple CacheControllers can point to the same caches this way.
            this.cache = globalCache.get(config.name);
        }
        else {
            // Otherwise, return a simple map tied directly to the method
            this.cache = newCache;
        }
        this.isOneItem = !config.idKey;
    }
    hasItem(id) {
        // It has the data if either the cached item exists, or if we want the whole cache map and it's not empty
        return this.isOneItem
            ? this.cache.has(id)
            : this.cache.size > 0;
    }
    // Expiring a cache means keep the current value, but fetch it next time it's requested
    expire(id) {
        this.setStatus(id, CacheStatus.Expired);
        // If we're expiring null, then we're expiring all cache objects
        if (id === null || id === undefined) {
            Array.from(this.cache.keys()).forEach(key => {
                this.setStatus(key, CacheStatus.Expired);
            });
        }
    }
    getStatus(id) {
        if (id === null || id === undefined || !this.isOneItem) {
            // If no id is passed, we get the status for the whole cache
            return this.status;
        }
        else if (this.cache.has(id)) {
            return this.cache.get(id).status;
        }
        else {
            return null;
        }
    }
    setStatus(id, status = CacheStatus.Normal) {
        if (id === null || id === undefined) {
            // If no id is passed, we set the status for the whole cache
            this.status = status;
        }
        else if (this.cache.has(id)) {
            this.cache.get(id).status = status;
        }
        else {
            this.cache.set(id, {
                status: status,
                value$: new BehaviorSubject(undefined),
            });
        }
    }
    setValue(id, value, status = CacheStatus.Normal) {
        if (this.cache.has(id)) {
            this.cache.get(id).status = status;
            this.cache.get(id).value$.next(value);
        }
        else {
            this.cache.set(id, {
                status: status,
                value$: new BehaviorSubject(value),
            });
        }
    }
    getValue(id, listen = false) {
        let values$;
        if (this.isOneItem) {
            values$ = this.cache.get(id).value$
                .pipe(filter(x => x !== undefined))
                .asObservable();
        }
        else {
            // If it's not one item then we're returning the whole cache
            let cacheValues = Array.from(this.cache.values()).map(c => c.value$);
            values$ = combineLatest(cacheValues.map(o => o
                .pipe(filter(x => x !== undefined))
                .asObservable()));
        }
        return iif(() => listen, values$, 
        // If the status is in flux for things we don't listen to, we have to keep listening until it's stable
        values$.pipe(takeWhile(val => this.getStatus(id) !== CacheStatus.Normal, true)));
    }
    removeItem(id) {
        this.cache.get(id).next(null);
        this.cache.get(id).complete();
        this.cache.delete(id);
    }
    overwrite(newValues, idKey, partial = false) {
        if (!partial) {
            this.removeDeleted(newValues, idKey);
            // We should only change the status for full updates so we partial updates don't block future full updates
            this.status = CacheStatus.Normal;
        }
        newValues.forEach(value => {
            let id = value[idKey];
            this.setValue(id, value);
        });
    }
    removeDeleted(newValues, idKey) {
        // List out the keys from the array of new values
        let newKeys = newValues.map(entry => entry[idKey]);
        // Check the list of our cache keys against the new data
        Array.from(this.cache.keys()).forEach(key => {
            if (!newKeys.includes(key)) {
                // Anything we no longer have gets set to null, completed, and removed
                this.removeItem(key);
            }
        });
    }
}

const DEFAULT_CACHE_OPTIONS = {
    update: false,
    listen: false,
};

// Marks the method parameter to indicate cache specific options
const cacheOptsMetadata = Symbol('CacheOptions');
function CacheOpts(target, propertyKey, parameterIndex) {
    Reflect.defineMetadata(cacheOptsMetadata, parameterIndex, target, propertyKey);
}
// Marks the method parameter to be used as the unique cache key. By default, all arguments are stringified.
const cacheKeyMetadata = Symbol('CacheKey');
function CacheKey(target, propertyKey, parameterIndex) {
    Reflect.defineMetadata(cacheKeyMetadata, parameterIndex, target, propertyKey);
}
// NOTE: This decorator is not unique per instance! Caches are shared among all instances!
function Cache(config = {}) {
    let cache = new CacheController(config);
    if (config.expireCache) {
        config.expireCache.subscribe(id => cache.expire(id));
    }
    // Bubble up the controller reference in case we need it
    config.cacheRef = cache;
    return function (service, methodName, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = function () {
            let args = arguments.length > 0 ? Array.from(arguments) : [];
            // Extract the cacheOpts param if one is provided
            let cacheOptsIndex = Reflect.getOwnMetadata(cacheOptsMetadata, service, methodName);
            // We splice it out because its sole purpose should be for cache logic
            let options = cacheOptsIndex >= 0 ? args.splice(cacheOptsIndex, 1)[0] : false;
            // Use the key param if one is specified
            let cacheKeyIndex = Reflect.getOwnMetadata(cacheKeyMetadata, service, methodName);
            // We keep it instead of splicing it out because it's likely used for other things (like the http request)
            let cacheKey = cacheKeyIndex >= 0 ? args[cacheKeyIndex] : JSON.stringify(args);
            // Merge with defaults
            options = Object.assign({}, DEFAULT_CACHE_OPTIONS, options);
            let cacheShouldUpdate = options.update
                || !cache.hasItem(cacheKey)
                || cache.getStatus(cache.isOneItem ? cacheKey : null) === CacheStatus.Expired
                || cache.getStatus(cache.isOneItem ? cacheKey : null) === CacheStatus.Initial;
            if (cacheShouldUpdate) {
                cache.setStatus(cache.isOneItem ? cacheKey : null, CacheStatus.Pending);
                let requestProcessed = false;
                let output = originalMethod.apply(this, args);
                if (!isObservable(output)) {
                    throw new Error('Cached values must be observables!');
                }
                return output
                    .pipe(catchError(response => {
                    requestProcessed = true;
                    if (response && response.status && [404, 403].includes(response.status)) {
                        // 404 or 403 mean that you don't have this feature or you don't have access, so we pass down null to the flatMap
                        return of(null);
                    }
                    // Otherwise we throw an error
                    return throwError(response);
                }), flatMap(value => {
                    requestProcessed = true;
                    // Set the value if it's a single value, overwrite all values if it's multiple
                    if (cache.isOneItem) {
                        cache.setValue(cacheKey, value);
                    }
                    else {
                        cache.overwrite(value, config.idKey);
                    }
                    return cache.getValue(cacheKey, options.listen);
                }), finalize(() => {
                    // If the HTTP call gets canceled by a route change, then catch and flatMap might never run.
                    // So if it wasn't processed by now, we should expire it to make sure we update next time
                    if (!requestProcessed && cache.hasItem(cacheKey)) {
                        cache.expire(cacheKey);
                    }
                }));
            }
            return cache.getValue(cacheKey, options.listen);
        };
        return descriptor;
    };
}

const placeholderChar = '_';
const strFunction = 'function';

const emptyArray = [];
const emptyString = '';
function conformToMask(rawValue = emptyString, mask = emptyArray, config = {}) {
    if (!isArray(mask)) {
        // If someone passes a function as the mask property, we should call the
        // function to get the mask array - Normally this is handled by the
        // `createTextMaskInputElement:update` function - this allows mask functions
        // to be used directly with `conformToMask`
        if (typeof mask === strFunction) {
            // call the mask function to get the mask array
            //@ts-ignore
            mask = mask(rawValue, config);
            // mask functions can setup caret traps to have some control over how the caret moves. We need to process
            // the mask for any caret traps. `processCaretTraps` will remove the caret traps from the mask
            mask = processCaretTraps(mask).maskWithoutCaretTraps;
        }
        else {
            throw new Error('Text-mask:conformToMask; The mask property must be an array.');
        }
    }
    // These configurations tell us how to conform the mask
    const { guide = true, previousConformedValue = emptyString, placeholderChar: placeholderChar$1 = placeholderChar, placeholder = convertMaskToPlaceholder(mask, placeholderChar$1), currentCaretPosition, keepCharPositions } = config;
    // The configs below indicate that the user wants the algorithm to work in *no guide* mode
    const suppressGuide = guide === false && previousConformedValue !== undefined;
    // Calculate lengths once for performance
    const rawValueLength = rawValue.length;
    const previousConformedValueLength = previousConformedValue.length;
    const placeholderLength = placeholder.length;
    const maskLength = mask.length;
    // This tells us the number of edited characters and the direction in which they were edited (+/-)
    const editDistance = rawValueLength - previousConformedValueLength;
    // In *no guide* mode, we need to know if the user is trying to add a character or not
    const isAddition = editDistance > 0;
    // Tells us the index of the first change. For (438) 394-4938 to (38) 394-4938, that would be 1
    const indexOfFirstChange = currentCaretPosition + (isAddition ? -editDistance : 0);
    // We're also gonna need the index of last change, which we can derive as follows...
    const indexOfLastChange = indexOfFirstChange + Math.abs(editDistance);
    // If `conformToMask` is configured to keep character positions, that is, for mask 111, previous value
    // _2_ and raw value 3_2_, the new conformed value should be 32_, not 3_2 (default behavior). That's in the case of
    // addition. And in the case of deletion, previous value _23, raw value _3, the new conformed string should be
    // __3, not _3_ (default behavior)
    //
    // The next block of logic handles keeping character positions for the case of deletion. (Keeping
    // character positions for the case of addition is further down since it is handled differently.)
    // To do this, we want to compensate for all characters that were deleted
    if (keepCharPositions === true && !isAddition) {
        // We will be storing the new placeholder characters in this variable.
        let compensatingPlaceholderChars = emptyString;
        // For every character that was deleted from a placeholder position, we add a placeholder char
        for (let i = indexOfFirstChange; i < indexOfLastChange; i++) {
            if (placeholder[i] === placeholderChar$1) {
                compensatingPlaceholderChars += placeholderChar$1;
            }
        }
        // Now we trick our algorithm by modifying the raw value to make it contain additional placeholder characters
        // That way when the we start laying the characters again on the mask, it will keep the non-deleted characters
        // in their positions.
        rawValue = (rawValue.slice(0, indexOfFirstChange) +
            compensatingPlaceholderChars +
            rawValue.slice(indexOfFirstChange, rawValueLength));
    }
    // Convert `rawValue` string to an array, and mark characters based on whether they are newly added or have
    // existed in the previous conformed value. Identifying new and old characters is needed for `conformToMask`
    // to work if it is configured to keep character positions.
    const rawValueArr = rawValue
        .split(emptyString)
        .map((char, i) => ({ char, isNew: i >= indexOfFirstChange && i < indexOfLastChange }));
    // The loop below removes masking characters from user input. For example, for mask
    // `00 (111)`, the placeholder would be `00 (___)`. If user input is `00 (234)`, the loop below
    // would remove all characters but `234` from the `rawValueArr`. The rest of the algorithm
    // then would lay `234` on top of the available placeholder positions in the mask.
    for (let i = rawValueLength - 1; i >= 0; i--) {
        const { char } = rawValueArr[i];
        if (char !== placeholderChar$1) {
            const shouldOffset = i >= indexOfFirstChange && previousConformedValueLength === maskLength;
            if (char === placeholder[(shouldOffset) ? i - editDistance : i]) {
                rawValueArr.splice(i, 1);
            }
        }
    }
    // This is the variable that we will be filling with characters as we figure them out
    // in the algorithm below
    let conformedValue = emptyString;
    let someCharsRejected = false;
    // Ok, so first we loop through the placeholder looking for placeholder characters to fill up.
    placeholderLoop: for (let i = 0; i < placeholderLength; i++) {
        const charInPlaceholder = placeholder[i];
        // We see one. Let's find out what we can put in it.
        if (charInPlaceholder === placeholderChar$1) {
            // But before that, do we actually have any user characters that need a place?
            if (rawValueArr.length > 0) {
                // We will keep chipping away at user input until either we run out of characters
                // or we find at least one character that we can map.
                while (rawValueArr.length > 0) {
                    // Let's retrieve the first user character in the queue of characters we have left
                    const { char: rawValueChar, isNew } = rawValueArr.shift();
                    // If the character we got from the user input is a placeholder character (which happens
                    // regularly because user input could be something like (540) 90_-____, which includes
                    // a bunch of `_` which are placeholder characters) and we are not in *no guide* mode,
                    // then we map this placeholder character to the current spot in the placeholder
                    if (rawValueChar === placeholderChar$1 && suppressGuide !== true) {
                        conformedValue += placeholderChar$1;
                        // And we go to find the next placeholder character that needs filling
                        continue placeholderLoop;
                        // Else if, the character we got from the user input is not a placeholder, let's see
                        // if the current position in the mask can accept it.
                    }
                    else if (mask[i].test(rawValueChar)) {
                        // we map the character differently based on whether we are keeping character positions or not.
                        // If any of the conditions below are met, we simply map the raw value character to the
                        // placeholder position.
                        if (keepCharPositions !== true ||
                            isNew === false ||
                            previousConformedValue === emptyString ||
                            guide === false ||
                            !isAddition) {
                            conformedValue += rawValueChar;
                        }
                        else {
                            // We enter this block of code if we are trying to keep character positions and none of the conditions
                            // above is met. In this case, we need to see if there's an available spot for the raw value character
                            // to be mapped to. If we couldn't find a spot, we will discard the character.
                            //
                            // For example, for mask `1111`, previous conformed value `_2__`, raw value `942_2__`. We can map the
                            // `9`, to the first available placeholder position, but then, there are no more spots available for the
                            // `4` and `2`. So, we discard them and end up with a conformed value of `92__`.
                            const rawValueArrLength = rawValueArr.length;
                            let indexOfNextAvailablePlaceholderChar = null;
                            // Let's loop through the remaining raw value characters. We are looking for either a suitable spot, ie,
                            // a placeholder character or a non-suitable spot, ie, a non-placeholder character that is not new.
                            // If we see a suitable spot first, we store its position and exit the loop. If we see a non-suitable
                            // spot first, we exit the loop and our `indexOfNextAvailablePlaceholderChar` will stay as `null`.
                            for (let i = 0; i < rawValueArrLength; i++) {
                                const charData = rawValueArr[i];
                                if (charData.char !== placeholderChar$1 && charData.isNew === false) {
                                    break;
                                }
                                if (charData.char === placeholderChar$1) {
                                    indexOfNextAvailablePlaceholderChar = i;
                                    break;
                                }
                            }
                            // If `indexOfNextAvailablePlaceholderChar` is not `null`, that means the character is not blocked.
                            // We can map it. And to keep the character positions, we remove the placeholder character
                            // from the remaining characters
                            if (indexOfNextAvailablePlaceholderChar !== null) {
                                conformedValue += rawValueChar;
                                rawValueArr.splice(indexOfNextAvailablePlaceholderChar, 1);
                                // If `indexOfNextAvailablePlaceholderChar` is `null`, that means the character is blocked. We have to
                                // discard it.
                            }
                            else {
                                i--;
                            }
                        }
                        // Since we've mapped this placeholder position. We move on to the next one.
                        continue placeholderLoop;
                    }
                    else {
                        someCharsRejected = true;
                    }
                }
            }
            // We reach this point when we've mapped all the user input characters to placeholder
            // positions in the mask. In *guide* mode, we append the left over characters in the
            // placeholder to the `conformedString`, but in *no guide* mode, we don't wanna do that.
            //
            // That is, for mask `(111)` and user input `2`, we want to return `(2`, not `(2__)`.
            if (suppressGuide === false) {
                conformedValue += placeholder.substr(i, placeholderLength);
            }
            // And we break
            break;
            // Else, the charInPlaceholder is not a placeholderChar. That is, we cannot fill it
            // with user input. So we just map it to the final output
        }
        else {
            conformedValue += charInPlaceholder;
        }
    }
    // The following logic is needed to deal with the case of deletion in *no guide* mode.
    //
    // Consider the silly mask `(111) /// 1`. What if user tries to delete the last placeholder
    // position? Something like `(589) /// `. We want to conform that to `(589`. Not `(589) /// `.
    // That's why the logic below finds the last filled placeholder character, and removes everything
    // from that point on.
    if (suppressGuide && isAddition === false) {
        let indexOfLastFilledPlaceholderChar = null;
        // Find the last filled placeholder position and substring from there
        for (let i = 0; i < conformedValue.length; i++) {
            if (placeholder[i] === placeholderChar$1) {
                indexOfLastFilledPlaceholderChar = i;
            }
        }
        if (indexOfLastFilledPlaceholderChar !== null) {
            // We substring from the beginning until the position after the last filled placeholder char.
            conformedValue = conformedValue.substr(0, indexOfLastFilledPlaceholderChar + 1);
        }
        else {
            // If we couldn't find `indexOfLastFilledPlaceholderChar` that means the user deleted
            // the first character in the mask. So we return an empty string.
            conformedValue = emptyString;
        }
    }
    return { conformedValue, meta: { someCharsRejected } };
}
function convertMaskToPlaceholder(mask = emptyArray, placeholderChar$1 = placeholderChar) {
    if (!isArray(mask)) {
        throw new Error('Text-mask:convertMaskToPlaceholder; The mask property must be an array.');
    }
    if (mask.indexOf(placeholderChar$1) !== -1) {
        throw new Error('Placeholder character must not be used as part of the mask. Please specify a character ' +
            'that is not present in your mask as your placeholder character.\n\n' +
            `The placeholder character that was received is: ${JSON.stringify(placeholderChar$1)}\n\n` +
            `The mask that was received is: ${JSON.stringify(mask)}`);
    }
    return mask.map((char) => {
        return (char instanceof RegExp) ? placeholderChar$1 : char;
    }).join('');
}
function isArray(value) {
    return (Array.isArray && Array.isArray(value)) || value instanceof Array;
}
const strCaretTrap = '[]';
function processCaretTraps(mask) {
    const indexes = [];
    let indexOfCaretTrap;
    while (indexOfCaretTrap = mask.indexOf(strCaretTrap), indexOfCaretTrap !== -1) { // eslint-disable-line
        indexes.push(indexOfCaretTrap);
        mask.splice(indexOfCaretTrap, 1);
    }
    return { maskWithoutCaretTraps: mask, indexes };
}

class TextMaskConfig {
}
const MASKEDINPUT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MaskedInputDirective),
    multi: true
};
/**
 * We must check whether the agent is Android because composition events
 * behave differently between iOS and Android.
 */
function _isAndroid() {
    const userAgent = ɵgetDOM() ? ɵgetDOM().getUserAgent() : '';
    return /android (\d+)/.test(userAgent.toLowerCase());
}
class MaskedInputDirective {
    constructor(_renderer, _elementRef, _compositionMode) {
        this._renderer = _renderer;
        this._elementRef = _elementRef;
        this._compositionMode = _compositionMode;
        this.textMaskConfig = {
            mask: [],
            guide: true,
            placeholderChar: '_',
            pipe: undefined,
            keepCharPositions: false,
        };
        this.onChange = (_) => { };
        this.onTouched = () => { };
        /** Whether the user is creating a composition string (IME events). */
        this._composing = false;
        if (this._compositionMode == null) {
            this._compositionMode = !_isAndroid();
        }
    }
    ngOnChanges(changes) {
        this._setupMask(true);
        if (this.textMaskInputElement !== undefined) {
            this.textMaskInputElement.update(this.inputElement.value);
        }
    }
    writeValue(value) {
        this._setupMask();
        // set the initial value for cases where the mask is disabled
        const normalizedValue = value == null ? '' : value;
        this._renderer.setProperty(this.inputElement, 'value', normalizedValue);
        if (this.textMaskInputElement !== undefined) {
            this.textMaskInputElement.update(value);
        }
    }
    registerOnChange(fn) { this.onChange = fn; }
    registerOnTouched(fn) { this.onTouched = fn; }
    setDisabledState(isDisabled) {
        this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
    }
    _handleInput(value) {
        if (!this._compositionMode || (this._compositionMode && !this._composing)) {
            this._setupMask();
            if (this.textMaskInputElement !== undefined) {
                this.textMaskInputElement.update(value);
                // get the updated value
                value = this.inputElement.value;
                this.onChange(value);
            }
        }
    }
    _setupMask(create = false) {
        if (!this.inputElement) {
            if (this._elementRef.nativeElement.tagName.toUpperCase() === 'INPUT') {
                // `textMask` directive is used directly on an input element
                this.inputElement = this._elementRef.nativeElement;
            }
            else {
                // `textMask` directive is used on an abstracted input element, `md-input-container`, etc
                this.inputElement = this._elementRef.nativeElement.getElementsByTagName('INPUT')[0];
            }
        }
        if (this.inputElement && create) {
            this.textMaskInputElement = createTextMaskInputElement(Object.assign({ inputElement: this.inputElement }, this.textMaskConfig));
        }
    }
    _compositionStart() { this._composing = true; }
    _compositionEnd(value) {
        this._composing = false;
        this._compositionMode && this._handleInput(value);
    }
    static { this.ɵfac = function MaskedInputDirective_Factory(t) { return new (t || MaskedInputDirective)(i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(COMPOSITION_BUFFER_MODE, 8)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: MaskedInputDirective, selectors: [["", "textMask", ""]], hostBindings: function MaskedInputDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("input", function MaskedInputDirective_input_HostBindingHandler($event) { return ctx._handleInput($event.target.value); })("blur", function MaskedInputDirective_blur_HostBindingHandler() { return ctx.onTouched(); })("compositionstart", function MaskedInputDirective_compositionstart_HostBindingHandler() { return ctx._compositionStart(); })("compositionend", function MaskedInputDirective_compositionend_HostBindingHandler($event) { return ctx._compositionEnd($event.target.value); });
        } }, inputs: { textMaskConfig: ["textMask", "textMaskConfig"] }, exportAs: ["textMask"], features: [i0.ɵɵProvidersFeature([MASKEDINPUT_VALUE_ACCESSOR]), i0.ɵɵNgOnChangesFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MaskedInputDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(input)': '_handleInput($event.target.value)',
                    '(blur)': 'onTouched()',
                    '(compositionstart)': '_compositionStart()',
                    '(compositionend)': '_compositionEnd($event.target.value)'
                },
                /* eslint-disable-next-line @angular-eslint/directive-selector */
                selector: '[textMask]',
                exportAs: 'textMask',
                providers: [MASKEDINPUT_VALUE_ACCESSOR]
            }]
    }], function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [COMPOSITION_BUFFER_MODE]
            }] }]; }, { textMaskConfig: [{
            type: Input,
            args: ['textMask']
        }] }); })();

// import { CanvasService } from './services/canvas.service';
const directives = [
    FormControlDirective,
    FormGroupDirective,
    SnapDirective,
    SnapContainerDirective,
    FocusMeDirective,
    RoleButtonDirective,
    RoleMenubarDirective,
    NavDirective,
    CloseMenuBtnDirective,
    CdnSrcDirective,
    AriaActiveLinkDirective,
    ClipboardDirective,
    TargetBlankDirective,
    H1Directive,
    ExpandableContentDirective,
    ExpandableContainerDirective,
    MaskedInputDirective
];
const pipes = [
    AddressPipe,
    BankAccountDescPipe,
    BirthdatePipe,
    EmploymentStatusPipe,
    FileSizePipe,
    KeysPipe,
    EntriesPipe,
    ListPipe,
    MomentPipe,
    PluralPipe,
    BlankPipe,
    OccupationPipe,
    OrdinalPipe,
    DotsPipe,
    SSNPipe,
    LastFourPipe,
    AAnPipe,
    ValuesPipe,
    CardNumberPipe,
    NamedIntegerPipe,
];
const services = [
    WindowRefService,
    PageFocusService,
    DialogService,
    StorageService,
    NotificationService,
    DiagnosisCodeOptionsService,
    // CanvasService,
];
function provideForRootGuard(windowRefService) {
    if (windowRefService) {
        throw new Error(`SumdayCommonModule.forRoot() called twice. Lazy loaded modules should use SumdayCommonModule.forChild() instead.`);
    }
    return 'guarded';
}
class SumdayCommonModule {
    static forRoot() {
        return {
            ngModule: SumdayCommonModule,
            providers: [
                ...services,
                {
                    provide: SUMDAY_COMMON_FORROOT_GUARD,
                    useFactory: provideForRootGuard,
                    deps: [[WindowRefService, new Optional(), new SkipSelf()]]
                },
                { provide: PAGE_TITLE_BASE_TEXT, useValue: new BehaviorSubject('') },
                { provide: H1_DEFAULT, useValue: { autoFocus: false, updatePageTitle: false } },
            ]
        };
    }
    static { this.ɵfac = function SumdayCommonModule_Factory(t) { return new (t || SumdayCommonModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: SumdayCommonModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SumdayCommonModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                ],
                exports: [
                    ...directives,
                    ...pipes,
                ],
                declarations: [
                    ...directives,
                    ...pipes,
                ],
                providers: [
                // Do not do this for shared modules see forRoot below
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SumdayCommonModule, { declarations: [FormControlDirective,
        FormGroupDirective,
        SnapDirective,
        SnapContainerDirective,
        FocusMeDirective,
        RoleButtonDirective,
        RoleMenubarDirective,
        NavDirective,
        CloseMenuBtnDirective,
        CdnSrcDirective,
        AriaActiveLinkDirective,
        ClipboardDirective,
        TargetBlankDirective,
        H1Directive,
        ExpandableContentDirective,
        ExpandableContainerDirective,
        MaskedInputDirective, AddressPipe,
        BankAccountDescPipe,
        BirthdatePipe,
        EmploymentStatusPipe,
        FileSizePipe,
        KeysPipe,
        EntriesPipe,
        ListPipe,
        MomentPipe,
        PluralPipe,
        BlankPipe,
        OccupationPipe,
        OrdinalPipe,
        DotsPipe,
        SSNPipe,
        LastFourPipe,
        AAnPipe,
        ValuesPipe,
        CardNumberPipe,
        NamedIntegerPipe], imports: [CommonModule], exports: [FormControlDirective,
        FormGroupDirective,
        SnapDirective,
        SnapContainerDirective,
        FocusMeDirective,
        RoleButtonDirective,
        RoleMenubarDirective,
        NavDirective,
        CloseMenuBtnDirective,
        CdnSrcDirective,
        AriaActiveLinkDirective,
        ClipboardDirective,
        TargetBlankDirective,
        H1Directive,
        ExpandableContentDirective,
        ExpandableContainerDirective,
        MaskedInputDirective, AddressPipe,
        BankAccountDescPipe,
        BirthdatePipe,
        EmploymentStatusPipe,
        FileSizePipe,
        KeysPipe,
        EntriesPipe,
        ListPipe,
        MomentPipe,
        PluralPipe,
        BlankPipe,
        OccupationPipe,
        OrdinalPipe,
        DotsPipe,
        SSNPipe,
        LastFourPipe,
        AAnPipe,
        ValuesPipe,
        CardNumberPipe,
        NamedIntegerPipe] }); })();

/*
 * Public API Surface of common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AAnPipe, API_BASE_PATH, ActivityParams, Address, AddressPipe, AriaActiveLinkDirective, BankAccount, BankAccountDescPipe, BankAccountType, BaseAccount, BirthdatePipe, BlankPipe, CDN_HOST, Cache, CacheController, CacheKey, CacheOpts, CacheStatus, CanvasGlobalComposition, CanvasService, CanvasTextAlign, CardNumberPipe, CdnSrcDirective, ChartType, ClipboardDirective, CloseMenuBtnDirective, ContentWalker, DEFAULT_ACTIVITY_PARAMS, DEFAULT_CACHE_OPTIONS, DateInterceptor, Deferred, DiagnosisCodeOption, DiagnosisCodeOptionsService, DialogService, DotsPipe, EmploymentStatus, EmploymentStatusDescription, EmploymentStatusPipe, EmploymentStatusText, EntriesPipe, ErrorItem, ErrorResponseWrapper, ExpandableContainerDirective, ExpandableContentDirective, ExtendedValidators, FileInterceptor, FileSizePipe, FocusMeDirective, FormControlDirective, FormGroupDirective, H1Directive, H1_DEFAULT, HttpUtils, KeyCodes, KeysPipe, LastFourPipe, ListPipe, MASKEDINPUT_VALUE_ACCESSOR, MaskedInputDirective, MockPageFocusService, MockWindowRefService, MomentPipe, NamedIntegerPipe, NavDirective, NotificationService, NotificationType, NumberUtils, OccupationPipe, OrdinalPipe, PAGE_TITLE_BASE_TEXT, PageFocusService, PaginatedData, PlanType, PluralPipe, ResponseMetadata, RoleButtonDirective, RoleMenubarDirective, SSNPipe, SUMDAY_COMMON_FORROOT_GUARD, SnapContainerDirective, SnapDirective, StorageService, SumdayCommonModule, SumdayTimer, TakeoverType, TargetBlankDirective, TextMaskConfig$1 as TextMaskConfig, TextMasks, ToggleType, TypeaheadConfigDefaults, Unsubscriber, ValidationErrorMap, ValidationService, ValuesPipe, WindowRefService, conformToMask, convertMaskToPlaceholder, countries, createNumberMask, hasRequiredField, isArray, isBlank, isPresent, militaryRegions, processCaretTraps, provideForRootGuard, states, statesWithMilitary, statesWithMilitaryAndTerritories, statesWithTerritories, territories };
